/* Luminix Health lab calculators. European number format.
   Acid molarity: (wt% × density g/mL × 10) / MW = mol/L.
   ppm is treated as mg/L, the dilute-water convention. */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  root.LxCalc = api;
  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", api.boot);
    } else {
      api.boot();
    }
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  var VOL = { L: 1, mL: 1e-3, "µL": 1e-6 };
  var CONC = {
    "mol/L": { dim: "molar", to: 1 },
    "mmol/L": { dim: "molar", to: 1e-3 },
    "µmol/L": { dim: "molar", to: 1e-6 },
    "nmol/L": { dim: "molar", to: 1e-9 },
    "g/L": { dim: "mass", to: 1 },
    "mg/mL": { dim: "mass", to: 1 },
    "mg/L": { dim: "mass", to: 1e-3 },
    "µg/mL": { dim: "mass", to: 1e-3 },
    "µg/L": { dim: "mass", to: 1e-6 },
    "ng/mL": { dim: "mass", to: 1e-6 },
    "% w/v": { dim: "mass", to: 10 },
    ppm: { dim: "mass", to: 1e-3 }
  };

  var ACIDS = [
    { id: "hcl", pct: 37, d: 1.19, mw: 36.46, n: 1 },
    { id: "h2so4", pct: 98, d: 1.84, mw: 98.08, n: 2 },
    { id: "hno3", pct: 70, d: 1.413, mw: 63.01, n: 1 },
    { id: "h3po4", pct: 85, d: 1.685, mw: 97.99, n: 3 },
    { id: "acetic", pct: 99.7, d: 1.049, mw: 60.05, n: 1 },
    { id: "nh3-25", pct: 25, d: 0.907, mw: 17.03, n: 1 },
    { id: "nh3-28", pct: 28, d: 0.899, mw: 17.03, n: 1 },
    { id: "naoh50", pct: 50, d: 1.525, mw: 40, n: 1 }
  ];

  var SALTS = [
    { id: "tris", mw: 121.14, pka: 8.07 },
    { id: "tris-hcl", mw: 157.6, pka: 8.07 },
    { id: "hepes", mw: 238.3, pka: 7.48 },
    { id: "hepes-na", mw: 260.29, pka: 7.48 },
    { id: "mops", mw: 209.26, pka: 7.2 },
    { id: "mes", mw: 195.24, pka: 6.15 },
    { id: "pipes", mw: 302.37, pka: 6.76 },
    { id: "na2hpo4", mw: 141.96, pka: 7.21 },
    { id: "nah2po4", mw: 119.98, pka: 7.21 },
    { id: "acetate", mw: 82.03, pka: 4.76 },
    { id: "glycine", mw: 75.07, pka: 9.6 },
    { id: "bicine", mw: 163.17, pka: 8.26 },
    { id: "caps", mw: 221.32, pka: 10.4 },
    { id: "nacl", mw: 58.44, pka: null },
    { id: "edta", mw: 372.24, pka: null }
  ];

  var PAIRS = [
    { id: "tris-pair", pka: 8.07, mwAcid: 157.6, mwBase: 121.14, acid: "tris-hcl", base: "tris" },
    { id: "phosphate", pka: 7.21, mwAcid: 119.98, mwBase: 141.96, acid: "nah2po4", base: "na2hpo4" },
    { id: "acetate-pair", pka: 4.76, mwAcid: 60.05, mwBase: 82.03, densityAcid: 1.049, acid: "acetic", base: "acetate" },
    { id: "hepes-pair", pka: 7.48, mwAcid: 238.3, mwBase: 260.29, acid: "hepes", base: "hepes-na" }
  ];

  var PBS = [
    { id: "nacl", mmol: 137, mw: 58.44 },
    { id: "kcl", mmol: 2.7, mw: 74.55 },
    { id: "na2hpo4", mmol: 10, mw: 141.96 },
    { id: "kh2po4", mmol: 1.8, mw: 136.09 }
  ];

  var COPY = {
    en: {
      calc: "Calculate",
      reset: "Clear",
      solve: "Calculate",
      mass: "Mass to weigh",
      concentration: "Concentration",
      volume: "Volume",
      mw: "Molar mass",
      conc: "Concentration",
      vol: "Volume",
      stock: "Stock",
      dilute: "Diluted solution",
      which: "Leave this one empty, or pick it here",
      c1: "Stock concentration",
      v1: "Stock volume",
      c2: "Target concentration",
      v2: "Final volume",
      percent: "Percent",
      kind: "Kind of percent",
      wv: "% (w/v), grams in 100 mL",
      vv: "% (v/v), millilitres in 100 mL",
      ww: "% (w/w), grams in 100 g",
      finalVol: "Final volume",
      finalMass: "Final mass",
      mode: "What to prepare",
      oneSalt: "One salt, then set the pH",
      pair: "Acid and base pair for a target pH",
      pbs: "Phosphate-buffered saline, without calcium or magnesium",
      salt: "Salt",
      ph: "Target pH",
      strength: "Strength",
      bottle: "Solution on the bottle",
      pct: "Weight percent",
      density: "Density",
      protons: "Protons counted for normality",
      target: "Target concentration",
      factor: "Dilution factor",
      steps: "Number of steps",
      tube: "Volume in each tube",
      from: "From",
      to: "To",
      value: "Value",
      errNum: "Enter a number greater than zero.",
      errMw: "Enter the molar mass. It has to be greater than zero.",
      errSame: "Use one kind of unit for both concentrations.",
      errWeak: "The stock is weaker than the target. This is not a dilution.",
      errFactor: "The factor has to be greater than 1.",
      errSteps: "Use 1 to 12 steps.",
      errPct: "Enter a percent above 0 and up to 100.",
      errPh: "Enter the target pH.",
      far: "This pH sits more than 1 unit from the pKa. The buffer has little capacity. The masses are still right.",
      ppm: "ppm is read as mg/L, which holds for dilute water.",
      weigh: "Weigh this mass. Dissolve it in less water than the final volume, then fill to the mark.",
      take: "Take this volume of stock and fill to the final volume.",
      soluteWv: "Weigh the solute and fill to the final volume.",
      soluteVv: "Measure the solute and fill to the final volume. Volumes are not always additive.",
      soluteWw: "Weigh the solute. The rest of the mixture is the other component.",
      phNote: "Dissolve the salt, set the pH with a meter, then fill to the mark. The pKa is the value at 25 °C.",
      pairNote: "The ratio follows the Henderson–Hasselbalch equation at 25 °C. Check the finished pH with a meter.",
      pbsNote: "Anhydrous salts. 1× PBS lands near pH 7,4. Check it with a meter.",
      acidNote: "These starting figures are typical for a common grade. Use the percent and the density printed on your bottle.",
      acidSafe: "Add concentrated acid to water. Do not add water to concentrated acid.",
      normality: "Normality counts the protons you entered. Molarity does not.",
      molarity: "Molarity of the bottle",
      normal: "Normality",
      stockTake: "Volume of stock",
      diluent: "Diluent, if the volumes add up",
      step: "Step",
      transfer: "Transfer",
      diluentCol: "Diluent",
      stockRow: "Stock",
      eqMass: "mass = concentration × molar mass × volume",
      aceticMl: "Acetic acid, if you pipette it",
      other: "Rest of the mixture"
    },
    nl: {
      calc: "Reken",
      reset: "Wis",
      solve: "Bereken",
      mass: "Massa om af te wegen",
      concentration: "Concentratie",
      volume: "Volume",
      mw: "Molaire massa",
      conc: "Concentratie",
      vol: "Volume",
      stock: "Stock",
      dilute: "Verdunde oplossing",
      which: "Laat dit veld leeg, of kies het hier",
      c1: "Concentratie stock",
      v1: "Volume stock",
      c2: "Gewenste concentratie",
      v2: "Eindvolume",
      percent: "Procent",
      kind: "Soort procent",
      wv: "% (m/v), gram per 100 mL",
      vv: "% (v/v), milliliter per 100 mL",
      ww: "% (m/m), gram per 100 g",
      finalVol: "Eindvolume",
      finalMass: "Eindmassa",
      mode: "Wat u maakt",
      oneSalt: "Eén zout, daarna de pH instellen",
      pair: "Zuur-basepaar voor een gewenste pH",
      pbs: "Fosfaatbuffer, zonder calcium of magnesium",
      salt: "Zout",
      ph: "Gewenste pH",
      strength: "Sterkte",
      bottle: "Oplossing op de fles",
      pct: "Massaprocent",
      density: "Dichtheid",
      protons: "Protonen voor de normaliteit",
      target: "Gewenste concentratie",
      factor: "Verdunningsfactor",
      steps: "Aantal stappen",
      tube: "Volume per buis",
      from: "Van",
      to: "Naar",
      value: "Waarde",
      errNum: "Vul een getal in dat groter is dan nul.",
      errMw: "Vul de molaire massa in. Die moet groter zijn dan nul.",
      errSame: "Gebruik één soort eenheid voor beide concentraties.",
      errWeak: "De stock is zwakker dan het doel. Dit is geen verdunning.",
      errFactor: "De factor moet groter zijn dan 1.",
      errSteps: "Gebruik 1 tot 12 stappen.",
      errPct: "Vul een procent in dat boven 0 ligt en hoogstens 100 is.",
      errPh: "Vul de gewenste pH in.",
      far: "Deze pH ligt meer dan 1 eenheid van de pKa. De buffer heeft weinig capaciteit. De massa's kloppen wel.",
      ppm: "ppm lezen we als mg/L. Dat geldt voor verdund water.",
      weigh: "Weeg deze massa af. Los op in minder water dan het eindvolume en vul aan tot de streep.",
      take: "Neem dit volume stock en vul aan tot het eindvolume.",
      soluteWv: "Weeg de opgeloste stof af en vul aan tot het eindvolume.",
      soluteVv: "Meet de opgeloste stof af en vul aan tot het eindvolume. Volumes zijn niet altijd optelbaar.",
      soluteWw: "Weeg de opgeloste stof af. De rest van het mengsel is de andere component.",
      phNote: "Los het zout op, stel de pH in met een meter en vul aan tot de streep. De pKa geldt bij 25 °C.",
      pairNote: "De verhouding volgt Henderson–Hasselbalch bij 25 °C. Controleer de pH met een meter.",
      pbsNote: "Watervrije zouten. 1× PBS komt uit rond pH 7,4. Controleer met een meter.",
      acidNote: "Deze startwaarden zijn typisch voor een gangbare kwaliteit. Gebruik het procent en de dichtheid van uw fles.",
      acidSafe: "Giet geconcentreerd zuur bij het water. Giet geen water bij geconcentreerd zuur.",
      normality: "De normaliteit telt de protonen die u invulde. De molariteit niet.",
      molarity: "Molariteit van de fles",
      normal: "Normaliteit",
      stockTake: "Volume stock",
      diluent: "Verdunningsmiddel, als de volumes optellen",
      step: "Stap",
      transfer: "Overbrengen",
      diluentCol: "Verdunningsmiddel",
      stockRow: "Stock",
      eqMass: "massa = concentratie × molaire massa × volume",
      aceticMl: "Azijnzuur, als u het pipetteert",
      other: "Rest van het mengsel"
    },
    fr: {
      calc: "Calculer",
      reset: "Effacer",
      solve: "Calculer",
      mass: "Masse à peser",
      concentration: "Concentration",
      volume: "Volume",
      mw: "Masse molaire",
      conc: "Concentration",
      vol: "Volume",
      stock: "Solution mère",
      dilute: "Solution diluée",
      which: "Laissez ce champ vide, ou choisissez-le ici",
      c1: "Concentration de la solution mère",
      v1: "Volume de solution mère",
      c2: "Concentration voulue",
      v2: "Volume final",
      percent: "Pourcentage",
      kind: "Type de pourcentage",
      wv: "% (m/v), grammes pour 100 mL",
      vv: "% (v/v), millilitres pour 100 mL",
      ww: "% (m/m), grammes pour 100 g",
      finalVol: "Volume final",
      finalMass: "Masse finale",
      mode: "Ce que vous préparez",
      oneSalt: "Un sel, puis régler le pH",
      pair: "Couple acide et base pour un pH voulu",
      pbs: "Tampon phosphate, sans calcium ni magnésium",
      salt: "Sel",
      ph: "pH voulu",
      strength: "Force",
      bottle: "Solution sur le flacon",
      pct: "Pourcentage massique",
      density: "Masse volumique",
      protons: "Protons comptés pour la normalité",
      target: "Concentration voulue",
      factor: "Facteur de dilution",
      steps: "Nombre d'étapes",
      tube: "Volume dans chaque tube",
      from: "De",
      to: "Vers",
      value: "Valeur",
      errNum: "Entrez un nombre supérieur à zéro.",
      errMw: "Entrez la masse molaire. Elle doit être supérieure à zéro.",
      errSame: "Utilisez le même type d'unité pour les deux concentrations.",
      errWeak: "La solution mère est plus faible que la cible. Ce n'est pas une dilution.",
      errFactor: "Le facteur doit être supérieur à 1.",
      errSteps: "Utilisez de 1 à 12 étapes.",
      errPct: "Entrez un pourcentage supérieur à 0 et d'au plus 100.",
      errPh: "Entrez le pH voulu.",
      far: "Ce pH est à plus d'une unité du pKa. Le tampon a peu de capacité. Les masses restent justes.",
      ppm: "Le ppm est lu comme mg/L, ce qui vaut pour une eau diluée.",
      weigh: "Pesez cette masse. Dissolvez-la dans moins d'eau que le volume final, puis ajustez au trait.",
      take: "Prélevez ce volume de solution mère et complétez au volume final.",
      soluteWv: "Pesez le soluté et complétez au volume final.",
      soluteVv: "Mesurez le soluté et complétez au volume final. Les volumes ne s'additionnent pas toujours.",
      soluteWw: "Pesez le soluté. Le reste du mélange est l'autre composant.",
      phNote: "Dissolvez le sel, réglez le pH au pH-mètre, puis ajustez au trait. Le pKa est donné à 25 °C.",
      pairNote: "Le rapport suit Henderson–Hasselbalch à 25 °C. Vérifiez le pH au pH-mètre.",
      pbsNote: "Sels anhydres. Le PBS 1× tombe près de pH 7,4. Vérifiez au pH-mètre.",
      acidNote: "Ces valeurs de départ sont typiques d'une qualité courante. Utilisez le pourcentage et la masse volumique de votre flacon.",
      acidSafe: "Versez l'acide concentré dans l'eau. Ne versez pas d'eau dans l'acide concentré.",
      normality: "La normalité compte les protons que vous avez saisis. La molarité non.",
      molarity: "Molarité du flacon",
      normal: "Normalité",
      stockTake: "Volume de solution mère",
      diluent: "Diluant, si les volumes s'additionnent",
      step: "Étape",
      transfer: "Transfert",
      diluentCol: "Diluant",
      stockRow: "Solution mère",
      eqMass: "masse = concentration × masse molaire × volume",
      aceticMl: "Acide acétique, si vous le pipettez",
      other: "Reste du mélange"
    }
  };

  var NAMES = {
    en: {
      hcl: "Hydrochloric acid, 37%",
      h2so4: "Sulfuric acid, 98%",
      hno3: "Nitric acid, 70%",
      h3po4: "Phosphoric acid, 85%",
      acetic: "Acetic acid, glacial",
      "nh3-25": "Ammonia, 25%",
      "nh3-28": "Ammonia, 28%",
      naoh50: "Sodium hydroxide, 50%",
      tris: "Tris base",
      "tris-hcl": "Tris hydrochloride",
      hepes: "HEPES, free acid",
      "hepes-na": "HEPES, sodium salt",
      mops: "MOPS",
      mes: "MES",
      pipes: "PIPES",
      na2hpo4: "Disodium hydrogen phosphate, anhydrous",
      nah2po4: "Sodium dihydrogen phosphate, anhydrous",
      acetate: "Sodium acetate, anhydrous",
      glycine: "Glycine",
      bicine: "Bicine",
      caps: "CAPS",
      nacl: "Sodium chloride",
      edta: "EDTA disodium, dihydrate",
      "tris-pair": "Tris hydrochloride and Tris base",
      phosphate: "Sodium dihydrogen phosphate and disodium hydrogen phosphate",
      "acetate-pair": "Acetic acid and sodium acetate",
      "hepes-pair": "HEPES and HEPES sodium salt",
      kcl: "Potassium chloride",
      kh2po4: "Potassium dihydrogen phosphate",
      "1x": "1×",
      "10x": "10×",
      custom: "Custom"
    },
    nl: {
      hcl: "Zoutzuur, 37%",
      h2so4: "Zwavelzuur, 98%",
      hno3: "Salpeterzuur, 70%",
      h3po4: "Fosforzuur, 85%",
      acetic: "Azijnzuur, ijsazijn",
      "nh3-25": "Ammonia, 25%",
      "nh3-28": "Ammonia, 28%",
      naoh50: "Natriumhydroxide, 50%",
      tris: "Tris-base",
      "tris-hcl": "Tris-hydrochloride",
      hepes: "HEPES, vrij zuur",
      "hepes-na": "HEPES, natriumzout",
      mops: "MOPS",
      mes: "MES",
      pipes: "PIPES",
      na2hpo4: "Dinatriumwaterstoffosfaat, watervrij",
      nah2po4: "Natriumdiwaterstoffosfaat, watervrij",
      acetate: "Natriumacetaat, watervrij",
      glycine: "Glycine",
      bicine: "Bicine",
      caps: "CAPS",
      nacl: "Natriumchloride",
      edta: "EDTA-dinatrium, dihydraat",
      "tris-pair": "Tris-hydrochloride en Tris-base",
      phosphate: "Natriumdiwaterstoffosfaat en dinatriumwaterstoffosfaat",
      "acetate-pair": "Azijnzuur en natriumacetaat",
      "hepes-pair": "HEPES en HEPES-natriumzout",
      kcl: "Kaliumchloride",
      kh2po4: "Kaliumdiwaterstoffosfaat",
      "1x": "1×",
      "10x": "10×",
      custom: "Zelf invullen"
    },
    fr: {
      hcl: "Acide chlorhydrique, 37 %",
      h2so4: "Acide sulfurique, 98 %",
      hno3: "Acide nitrique, 70 %",
      h3po4: "Acide phosphorique, 85 %",
      acetic: "Acide acétique glacial",
      "nh3-25": "Ammoniaque, 25 %",
      "nh3-28": "Ammoniaque, 28 %",
      naoh50: "Hydroxyde de sodium, 50 %",
      tris: "Tris base",
      "tris-hcl": "Chlorhydrate de Tris",
      hepes: "HEPES, acide libre",
      "hepes-na": "HEPES, sel de sodium",
      mops: "MOPS",
      mes: "MES",
      pipes: "PIPES",
      na2hpo4: "Hydrogénophosphate de disodium, anhydre",
      nah2po4: "Dihydrogénophosphate de sodium, anhydre",
      acetate: "Acétate de sodium, anhydre",
      glycine: "Glycine",
      bicine: "Bicine",
      caps: "CAPS",
      nacl: "Chlorure de sodium",
      edta: "EDTA disodique, dihydraté",
      "tris-pair": "Chlorhydrate de Tris et Tris base",
      phosphate: "Dihydrogénophosphate de sodium et hydrogénophosphate de disodium",
      "acetate-pair": "Acide acétique et acétate de sodium",
      "hepes-pair": "HEPES et sel de sodium du HEPES",
      kcl: "Chlorure de potassium",
      kh2po4: "Dihydrogénophosphate de potassium",
      "1x": "1×",
      "10x": "10×",
      custom: "Saisie libre"
    }
  };

  var EXTRA_T = {
    en: {
      radius: "Radius to the pellet", rpm: "Speed", rcf: "Relative centrifugal force",
      reactions: "Reactions", overage: "Extra volume", perRx: "Per reaction",
      absorbance: "Absorbance", transmittance: "Transmittance",
      temperature: "Temperature", columnLength: "Column length", diameter: "Internal diameter",
      particle: "Particle size", flow: "Flow", injection: "Injection volume", gradient: "Gradient time",
      fromCol: "Current column", toCol: "New column", mesh: "Sieve", opening: "Opening",
      gauge: "AWG", compound: "Compound", waterFill: "Water, then fill to the mark",
      addFresh: "Add this just before use.", phAdjust: "Set the pH with a meter.",
      rangeWarn: "This temperature sits outside the fitted range. Treat the result as a guide.",
      nearest: "Nearest sieve", dyeFilter: "Filter the list", ex: "Excitation", em: "Emission",
      solveFor: "Calculate", pressure: "Vapour pressure", kindOf: "Quantity"
    },
    nl: {
      radius: "Straal tot de pellet", rpm: "Toerental", rcf: "Relatieve centrifugale kracht",
      reactions: "Reacties", overage: "Extra volume", perRx: "Per reactie",
      absorbance: "Absorptie", transmittance: "Transmissie",
      temperature: "Temperatuur", columnLength: "Kolomlengte", diameter: "Inwendige diameter",
      particle: "Deeltjesgrootte", flow: "Flow", injection: "Injectievolume", gradient: "Gradiënttijd",
      fromCol: "Huidige kolom", toCol: "Nieuwe kolom", mesh: "Zeef", opening: "Opening",
      gauge: "AWG", compound: "Stof", waterFill: "Water, daarna aanvullen tot de streep",
      addFresh: "Voeg dit toe vlak voor gebruik.", phAdjust: "Stel de pH in met een meter.",
      rangeWarn: "Deze temperatuur valt buiten het geldige bereik. Zie het resultaat als een leidraad.",
      nearest: "Dichtstbijzijnde zeef", dyeFilter: "Filter de lijst", ex: "Excitatie", em: "Emissie",
      solveFor: "Bereken", pressure: "Dampdruk", kindOf: "Grootheid"
    },
    fr: {
      radius: "Rayon jusqu'au culot", rpm: "Vitesse", rcf: "Force centrifuge relative",
      reactions: "Réactions", overage: "Volume en plus", perRx: "Par réaction",
      absorbance: "Absorbance", transmittance: "Transmittance",
      temperature: "Température", columnLength: "Longueur de colonne", diameter: "Diamètre interne",
      particle: "Taille de particule", flow: "Débit", injection: "Volume d'injection", gradient: "Temps de gradient",
      fromCol: "Colonne actuelle", toCol: "Nouvelle colonne", mesh: "Tamis", opening: "Ouverture",
      gauge: "AWG", compound: "Composé", waterFill: "Eau, puis compléter au trait",
      addFresh: "Ajoutez ceci juste avant l'emploi.", phAdjust: "Réglez le pH au pH-mètre.",
      rangeWarn: "Cette température sort de la plage ajustée. Prenez le résultat comme un repère.",
      nearest: "Tamis le plus proche", dyeFilter: "Filtrer la liste", ex: "Excitation", em: "Émission",
      solveFor: "Calculer", pressure: "Pression de vapeur", kindOf: "Grandeur"
    }
  };

  var EXTRA_N = {
    en: {
      milk: "Non-fat dry milk", bsa: "Bovine serum albumin", sdsSalt: "SDS", glycerol: "Glycerol",
      bpb: "Bromophenol blue", bme: "2-Mercaptoethanol", glycine: "Glycine", tween: "Polysorbate 20",
      tris1m: "1 mol/L Tris-HCl, pH 6,8", sds10: "10% SDS", tris05: "0,5 mol/L Tris-HCl, pH 6,8",
      water: "Water", sucrose: "Sucrose", kno3: "Potassium nitrate", nano3: "Sodium nitrate"
    },
    nl: {
      milk: "Magere melkpoeder", bsa: "Runderserumalbumine", sdsSalt: "SDS", glycerol: "Glycerol",
      bpb: "Bromofenolblauw", bme: "2-Mercapto-ethanol", glycine: "Glycine", tween: "Polysorbaat 20",
      tris1m: "1 mol/L Tris-HCl, pH 6,8", sds10: "10% SDS", tris05: "0,5 mol/L Tris-HCl, pH 6,8",
      water: "Water", sucrose: "Sucrose", kno3: "Kaliumnitraat", nano3: "Natriumnitraat"
    },
    fr: {
      milk: "Lait écrémé en poudre", bsa: "Sérumalbumine bovine", sdsSalt: "SDS", glycerol: "Glycérol",
      bpb: "Bleu de bromophénol", bme: "2-Mercaptoéthanol", glycine: "Glycine", tween: "Polysorbate 20",
      tris1m: "Tris-HCl 1 mol/L, pH 6,8", sds10: "SDS 10 %", tris05: "Tris-HCl 0,5 mol/L, pH 6,8",
      water: "Eau", sucrose: "Saccharose", kno3: "Nitrate de potassium", nano3: "Nitrate de sodium"
    }
  };

  function localePack(code) {
    var loc = code === "nl" || code === "fr" ? code : "en";
    var t = {};
    var n = {};
    var k;
    for (k in COPY.en) t[k] = COPY.en[k];
    for (k in EXTRA_T.en) t[k] = EXTRA_T.en[k];
    for (k in COPY[loc]) t[k] = COPY[loc][k];
    for (k in EXTRA_T[loc]) t[k] = EXTRA_T[loc][k];
    for (k in NAMES.en) n[k] = NAMES.en[k];
    for (k in EXTRA_N.en) n[k] = EXTRA_N.en[k];
    for (k in NAMES[loc]) n[k] = NAMES[loc][k];
    for (k in EXTRA_N[loc]) n[k] = EXTRA_N[loc][k];
    return { t: t, n: n, loc: loc };
  }

  function parseNum(raw) {
    if (raw == null) return null;
    var s = String(raw).trim().replace(/\s/g, "").replace(/\u00a0/g, "").replace(/−/g, "-");
    if (!s) return null;
    if (s.indexOf(",") !== -1) s = s.replace(/\./g, "").replace(",", ".");
    else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
    var n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  function roundSig(n, figs) {
    if (n === 0) return 0;
    var p = Math.floor(Math.log10(Math.abs(n)));
    var m = Math.pow(10, figs - 1 - p);
    return Math.round(n * m) / m;
  }

  function sup(exp) {
    var map = { "-": "⁻", "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹" };
    return String(exp).split("").map(function (ch) { return map[ch] || ch; }).join("");
  }

  function formatPlain(n) {
    var neg = n < 0;
    var s = Math.abs(n).toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
    var parts = s.split(".");
    var ii = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    var out = parts[1] ? ii + "," + parts[1] : ii;
    return neg ? "−" + out : out;
  }

  function formatEU(n) {
    if (!Number.isFinite(n)) return "—";
    if (n === 0) return "0";
    var sign = n < 0 ? "−" : "";
    var a = Math.abs(n);
    if (a >= 1e6 || a < 1e-4) {
      var exp = Math.floor(Math.log10(a));
      var mant = roundSig(a / Math.pow(10, exp), 4);
      return sign + formatPlain(mant) + " × 10" + sup(exp);
    }
    return sign + formatPlain(roundSig(a, 6));
  }

  function pos(n) {
    return n != null && n > 0;
  }

  function toLitres(value, unit) {
    return value * VOL[unit];
  }

  function fromLitres(litres, unit) {
    return litres / VOL[unit];
  }

  function massFromMolar(mw, molPerL, litres) {
    return molPerL * mw * litres;
  }

  function molarityOfBottle(pct, density, mw) {
    return (pct * density * 10) / mw;
  }

  function bufferPair(pH, pKa, molar, litres, mwAcid, mwBase) {
    var ratio = Math.pow(10, pH - pKa);
    var fracBase = ratio / (1 + ratio);
    var moles = molar * litres;
    return {
      massAcid: moles * (1 - fracBase) * mwAcid,
      massBase: moles * fracBase * mwBase,
      far: Math.abs(pH - pKa) > 1
    };
  }

  function serialRows(c0, factor, steps, tube) {
    var transfer = tube / factor;
    var diluent = tube - transfer;
    var rows = [];
    for (var i = 1; i <= steps; i++) {
      rows.push({ step: i, conc: c0 / Math.pow(factor, i), transfer: transfer, diluent: diluent });
    }
    return { transfer: transfer, diluent: diluent, rows: rows };
  }

  function toGramsPerL(value, unit, mw) {
    var spec = CONC[unit];
    if (spec.dim === "mass") return value * spec.to;
    if (!pos(mw)) return null;
    return value * spec.to * mw;
  }

  function fromGramsPerL(gPerL, unit, mw) {
    var spec = CONC[unit];
    if (spec.dim === "mass") return gPerL / spec.to;
    if (!pos(mw)) return null;
    return gPerL / mw / spec.to;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === "class") node.className = attrs[k];
      else if (k === "text") node.textContent = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  function field(label, control) {
    var wrap = el("div", { class: "lx-calc__field" });
    wrap.appendChild(el("label", { text: label, for: control.id }));
    wrap.appendChild(control);
    return wrap;
  }

  function input(id, name, example) {
    var node = el("input", {
      id: id,
      name: name,
      class: "lx-calc__input",
      inputmode: "decimal",
      autocomplete: "off",
      placeholder: example || "0,1"
    });
    if (example) node.value = example;
    return node;
  }

  function select(id, name, options) {
    var node = el("select", { id: id, name: name, class: "lx-calc__input" });
    options.forEach(function (opt) {
      var o = el("option", { value: opt[0], text: opt[1] });
      node.appendChild(o);
    });
    return node;
  }

  function inline(left, right) {
    var row = el("div", { class: "lx-calc__inline" });
    row.appendChild(left);
    row.appendChild(right);
    return row;
  }

  var MOL = [["mol/L", "mol/L"], ["mmol/L", "mmol/L"], ["µmol/L", "µmol/L"], ["nmol/L", "nmol/L"]];
  var VOLU = [["L", "L"], ["mL", "mL"], ["µL", "µL"]];
  var DIL = MOL.concat([["g/L", "g/L"], ["mg/mL", "mg/mL"], ["% w/v", "% (w/v)"]]);
  var ALLU = Object.keys(CONC).map(function (u) {
    return [u, u === "% w/v" ? "% (w/v)" : u];
  });

  function resultBox() {
    return el("div", { class: "lx-calc__out", role: "status", "aria-live": "polite" });
  }

  function show(box, payload, t) {
    box.textContent = "";
    if (payload.error) {
      box.appendChild(el("p", { class: "lx-calc__err", text: payload.error }));
      return;
    }
    if (payload.value != null) {
      var big = el("p", { class: "lx-calc__value", text: payload.value });
      box.appendChild(big);
    }
    (payload.lines || []).forEach(function (line) {
      box.appendChild(el("p", { text: line }));
    });
    if (payload.table) {
      var table = el("table");
      var head = el("tr");
      payload.table.head.forEach(function (h) { head.appendChild(el("th", { text: h })); });
      table.appendChild(head);
      payload.table.rows.forEach(function (row) {
        var tr = el("tr");
        row.forEach(function (cell) { tr.appendChild(el("td", { text: cell })); });
        table.appendChild(tr);
      });
      box.appendChild(table);
    }
    if (payload.warn) box.appendChild(el("p", { class: "lx-calc__warn", text: payload.warn }));
    if (payload.note) box.appendChild(el("p", { class: "lx-calc__note", text: payload.note }));
  }

  function buttons(t) {
    var row = el("div", { class: "lx-calc__actions" });
    row.appendChild(el("button", { type: "submit", class: "lx-calc__btn", text: t.calc }));
    row.appendChild(el("button", { type: "reset", class: "lx-calc__reset", text: t.reset }));
    return row;
  }

  function boot() {
    document.querySelectorAll("[data-lx-tool]").forEach(function (mount) {
      if (mount.getAttribute("data-lx-ready") === "1") return;
      var tool = mount.getAttribute("data-lx-tool");
      if (!tool || tool === "hub") return;
      var pack = localePack(mount.getAttribute("data-lx-locale"));
      mount.textContent = "";
      if (tool === "periodic") {
        mount.classList.add("notranslate");
        mount.setAttribute("translate", "no");
        buildPeriodic(mount, null, pack);
        mount.setAttribute("data-lx-ready", "1");
        return;
      }
      var form = el("form", { class: "lx-calc__form", novalidate: "novalidate" });
      var out = resultBox();
      if (tool === "molarity") buildMolarity(form, out, pack);
      else if (tool === "dilution") buildDilution(form, out, pack);
      else if (tool === "percent") buildPercent(form, out, pack);
      else if (tool === "buffer") buildBuffer(form, out, pack);
      else if (tool === "acid") buildAcid(form, out, pack);
      else if (tool === "serial") buildSerial(form, out, pack);
      else if (tool === "units") buildUnits(form, out, pack);
      else if (tool === "gforce") buildGforce(form, out, pack);
      else if (tool === "pcr") buildPcr(form, out, pack);
      else if (tool === "dyes") buildDyes(form, out, pack);
      else if (tool === "absorbance") buildAbsorbance(form, out, pack);
      else if (tool === "measure") buildMeasure(form, out, pack, null);
      else if (tool === "length" || tool === "temp" || tool === "density" || tool === "massw" || tool === "flow" || tool === "pressure" || tool === "volumew" || tool === "torque") buildMeasure(form, out, pack, tool);
      else if (tool === "hplc") buildHplc(form, out, pack);
      else if (tool === "mesh") buildMesh(form, out, pack);
      else if (tool === "wire") buildWire(form, out, pack);
      else if (tool === "antoine") buildAntoine(form, out, pack);
      else if (tool === "solubility") buildSolubility(form, out, pack);
      else if (RECIPES[tool]) buildRecipe(form, out, pack, tool);
      form.addEventListener("reset", function () {
        window.setTimeout(function () { out.textContent = ""; }, 0);
      });
      mount.appendChild(form);
      mount.setAttribute("data-lx-ready", "1");
    });
  }

  function val(form, name) {
    var node = form.elements[name];
    return node ? node.value : "";
  }

  function buildMolarity(form, out, pack) {
    var t = pack.t;
    var solve = select("lx-solve", "solve", [["mass", t.mass], ["conc", t.concentration], ["vol", t.volume]]);
    var massField = field(t.mass, inline(input("lx-mass", "mass", "58,44"), el("span", { class: "lx-calc__unit", text: "g" })));
    var concField = field(t.conc, inline(input("lx-conc", "conc", "1"), select("lx-cu", "concUnit", MOL)));
    var volField = field(t.vol, inline(input("lx-vol", "vol", "1"), select("lx-vu", "volUnit", VOLU)));
    form.appendChild(field(t.solve, solve));
    form.appendChild(field(t.mw, inline(input("lx-mw", "mw", "58,44"), el("span", { class: "lx-calc__unit", text: "g/mol" }))));
    form.appendChild(massField);
    form.appendChild(concField);
    form.appendChild(volField);
    form.appendChild(buttons(t));
    form.appendChild(out);
    function sync() {
      massField.hidden = solve.value === "mass";
      concField.hidden = solve.value === "conc";
      volField.hidden = solve.value === "vol";
    }
    solve.addEventListener("change", sync);
    sync();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var mw = parseNum(val(form, "mw"));
      var mode = val(form, "solve");
      var cu = val(form, "concUnit");
      var vu = val(form, "volUnit");
      if (!pos(mw)) return show(out, { error: t.errMw });
      if (mode === "mass") {
        var conc = parseNum(val(form, "conc"));
        var vol = parseNum(val(form, "vol"));
        if (!pos(conc) || !pos(vol)) return show(out, { error: t.errNum });
        var molPerL = conc * CONC[cu].to;
        var litres = toLitres(vol, vu);
        var grams = massFromMolar(mw, molPerL, litres);
        show(out, {
          value: formatEU(grams) + " g",
          lines: [t.weigh, formatEU(mw) + " g/mol × " + formatEU(molPerL) + " mol/L × " + formatEU(litres) + " L = " + formatEU(grams) + " g"],
          note: t.eqMass
        });
        return;
      }
      if (mode === "conc") {
        var mass = parseNum(val(form, "mass"));
        var vol2 = parseNum(val(form, "vol"));
        if (!pos(mass) || !pos(vol2)) return show(out, { error: t.errNum });
        var litres2 = toLitres(vol2, vu);
        var shown = (mass / mw / litres2) / CONC[cu].to;
        show(out, {
          value: formatEU(shown) + " " + cu,
          lines: [formatEU(mass) + " g ÷ " + formatEU(mw) + " g/mol ÷ " + formatEU(litres2) + " L"]
        });
        return;
      }
      var mass2 = parseNum(val(form, "mass"));
      var conc2 = parseNum(val(form, "conc"));
      if (!pos(mass2) || !pos(conc2)) return show(out, { error: t.errNum });
      var mol2 = conc2 * CONC[cu].to;
      var shownV = fromLitres(mass2 / mw / mol2, vu);
      show(out, {
        value: formatEU(shownV) + " " + vu,
        lines: [formatEU(mass2) + " g ÷ (" + formatEU(mw) + " g/mol × " + formatEU(mol2) + " mol/L)"]
      });
    });
  }

  function buildDilution(form, out, pack) {
    var t = pack.t;
    var solve = select("lx-dsolve", "solve", [["v1", t.v1], ["c1", t.c1], ["c2", t.c2], ["v2", t.v2]]);
    form.appendChild(field(t.solve, solve));
    form.appendChild(field(t.c1, inline(input("lx-c1", "c1", "12"), select("lx-c1u", "c1u", DIL))));
    form.appendChild(field(t.v1, inline(input("lx-v1", "v1", ""), select("lx-v1u", "v1u", VOLU))));
    form.appendChild(field(t.c2, inline(input("lx-c2", "c2", "0,1"), select("lx-c2u", "c2u", DIL))));
    form.appendChild(field(t.v2, inline(input("lx-v2", "v2", "100"), select("lx-v2u", "v2u", VOLU))));
    form.querySelector("#lx-v1u").value = "mL";
    form.querySelector("#lx-v2u").value = "mL";
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var mode = val(form, "solve");
      var c1 = parseNum(val(form, "c1"));
      var v1 = parseNum(val(form, "v1"));
      var c2 = parseNum(val(form, "c2"));
      var v2 = parseNum(val(form, "v2"));
      var u1 = val(form, "c1u");
      var u2 = val(form, "c2u");
      if (CONC[u1].dim !== CONC[u2].dim) return show(out, { error: t.errSame });
      function baseC(v, u) { return v * CONC[u].to; }
      var known = { c1: c1, v1: v1, c2: c2, v2: v2 };
      if (!pos(known.c1) && mode !== "c1") return show(out, { error: t.errNum });
      if (!pos(known.v1) && mode !== "v1") return show(out, { error: t.errNum });
      if (!pos(known.c2) && mode !== "c2") return show(out, { error: t.errNum });
      if (!pos(known.v2) && mode !== "v2") return show(out, { error: t.errNum });
      var C1 = mode === "c1" ? null : baseC(c1, u1);
      var V1 = mode === "v1" ? null : toLitres(v1, val(form, "v1u"));
      var C2 = mode === "c2" ? null : baseC(c2, u2);
      var V2 = mode === "v2" ? null : toLitres(v2, val(form, "v2u"));
      var answer, unit;
      if (mode === "v1") {
        if (C2 > C1) return show(out, { error: t.errWeak });
        answer = fromLitres(C2 * V2 / C1, val(form, "v1u"));
        unit = val(form, "v1u");
      } else if (mode === "v2") {
        answer = fromLitres(C1 * V1 / C2, val(form, "v2u"));
        unit = val(form, "v2u");
      } else if (mode === "c1") {
        var raw = (C2 * V2 / V1) / CONC[u1].to;
        answer = raw;
        unit = u1;
      } else {
        answer = (C1 * V1 / V2) / CONC[u2].to;
        unit = u2;
      }
      if (!Number.isFinite(answer) || answer <= 0) return show(out, { error: t.errNum });
      show(out, {
        value: formatEU(answer) + " " + (unit === "% w/v" ? "% (w/v)" : unit),
        lines: [mode === "v1" ? t.take : "C₁V₁ = C₂V₂"],
        note: "C₁V₁ = C₂V₂"
      });
    });
  }

  function buildPercent(form, out, pack) {
    var t = pack.t;
    form.appendChild(field(t.kind, select("lx-kind", "kind", [["wv", t.wv], ["vv", t.vv], ["ww", t.ww]])));
    form.appendChild(field(t.percent, inline(input("lx-pct", "pct", "5"), el("span", { class: "lx-calc__unit", text: "%" }))));
    var amountLabel = field(t.finalVol, inline(input("lx-amt", "amt", "250"), select("lx-au", "au", VOLU)));
    form.appendChild(amountLabel);
    form.querySelector("#lx-au").value = "mL";
    form.appendChild(buttons(t));
    form.appendChild(out);
    function syncKind() {
      var kind = val(form, "kind");
      var lab = amountLabel.querySelector("label");
      var unit = form.querySelector("#lx-au");
      if (kind === "ww") {
        lab.textContent = t.finalMass;
        unit.innerHTML = "";
        [["g", "g"], ["kg", "kg"]].forEach(function (o) {
          unit.appendChild(el("option", { value: o[0], text: o[1] }));
        });
      } else {
        lab.textContent = t.finalVol;
        unit.innerHTML = "";
        VOLU.forEach(function (o) { unit.appendChild(el("option", { value: o[0], text: o[1] })); });
        unit.value = "mL";
      }
    }
    form.querySelector("#lx-kind").addEventListener("change", syncKind);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var pct = parseNum(val(form, "pct"));
      var amt = parseNum(val(form, "amt"));
      var kind = val(form, "kind");
      if (pct == null || pct <= 0 || pct > 100) return show(out, { error: t.errPct });
      if (!pos(amt)) return show(out, { error: t.errNum });
      if (kind === "ww") {
        var gramsTotal = val(form, "au") === "kg" ? amt * 1000 : amt;
        var solute = pct / 100 * gramsTotal;
        show(out, {
          value: formatEU(solute) + " g",
          lines: [t.soluteWw, t.other + ": " + formatEU(gramsTotal - solute) + " g"]
        });
      } else {
        var mL = fromLitres(toLitres(amt, val(form, "au")), "mL");
        var part = pct / 100 * mL;
        if (kind === "wv") {
          show(out, { value: formatEU(part) + " g", lines: [t.soluteWv, formatEU(pct) + " g / 100 mL × " + formatEU(mL) + " mL"] });
        } else {
          show(out, { value: formatEU(part) + " mL", lines: [t.soluteVv] });
        }
      }
    });
  }

  function buildBuffer(form, out, pack) {
    var t = pack.t;
    var n = pack.n;
    var mode = select("lx-bmode", "mode", [["salt", t.oneSalt], ["pair", t.pair], ["pbs", t.pbs]]);
    form.appendChild(field(t.mode, mode));
    var salt = select("lx-salt", "salt", SALTS.map(function (s) { return [s.id, n[s.id]]; }));
    var saltField = field(t.salt, salt);
    form.appendChild(saltField);
    var mwField = field(t.mw, inline(input("lx-bmw", "mw", "121,14"), el("span", { class: "lx-calc__unit", text: "g/mol" })));
    form.appendChild(mwField);
    var pair = select("lx-pair", "pair", PAIRS.map(function (p) { return [p.id, n[p.id]]; }));
    var pairField = field(t.pair, pair);
    form.appendChild(pairField);
    var phField = field(t.ph, input("lx-ph", "ph", "7,4"));
    form.appendChild(phField);
    form.appendChild(field(t.conc, inline(input("lx-bconc", "conc", "0,1"), select("lx-bcu", "cu", MOL))));
    var volField = field(t.vol, inline(input("lx-bvol", "vol", "1"), select("lx-bvu", "vu", VOLU)));
    form.appendChild(volField);
    var pbsStrength = field(t.strength, select("lx-px", "px", [["1", n["1x"]], ["10", n["10x"]]]));
    form.appendChild(pbsStrength);
    form.appendChild(buttons(t));
    form.appendChild(out);
    function applySalt() {
      var row = SALTS.filter(function (s) { return s.id === salt.value; })[0];
      form.elements.mw.value = formatEU(row.mw);
    }
    function sync() {
      var m = mode.value;
      saltField.hidden = m !== "salt";
      mwField.hidden = m !== "salt";
      pairField.hidden = m !== "pair";
      phField.hidden = m !== "pair";
      form.querySelector("#lx-bconc").closest(".lx-calc__field").hidden = m === "pbs";
      pbsStrength.hidden = m !== "pbs";
    }
    salt.addEventListener("change", applySalt);
    mode.addEventListener("change", sync);
    applySalt();
    sync();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var m = mode.value;
      var vol = parseNum(val(form, "vol"));
      if (!pos(vol)) return show(out, { error: t.errNum });
      var litres = toLitres(vol, val(form, "vu"));
      if (m === "pbs") {
        var fold = Number(val(form, "px"));
        var lines = PBS.map(function (row) {
          var grams = row.mmol / 1000 * fold * row.mw * litres;
          return n[row.id] + ": " + formatEU(grams) + " g";
        });
        show(out, { value: n[fold === 10 ? "10x" : "1x"] + " PBS", lines: lines, note: t.pbsNote });
        return;
      }
      if (m === "salt") {
        var conc = parseNum(val(form, "conc"));
        var mw = parseNum(val(form, "mw"));
        if (!pos(conc) || !pos(mw)) return show(out, { error: t.errNum });
        var mol = conc * CONC[val(form, "cu")].to;
        var grams = massFromMolar(mw, mol, litres);
        var saltRow = SALTS.filter(function (s) { return s.id === salt.value; })[0];
        var lines = [t.weigh];
        if (saltRow.pka) lines.push("pKa 25 °C: " + formatEU(saltRow.pka));
        show(out, { value: formatEU(grams) + " g", lines: lines, note: t.phNote });
        return;
      }
      var pH = parseNum(val(form, "ph"));
      var conc2 = parseNum(val(form, "conc"));
      if (pH == null) return show(out, { error: t.errPh });
      if (!pos(conc2)) return show(out, { error: t.errNum });
      var preset = PAIRS.filter(function (p) { return p.id === pair.value; })[0];
      var got = bufferPair(pH, preset.pka, conc2 * CONC[val(form, "cu")].to, litres, preset.mwAcid, preset.mwBase);
      var lines = [
        n[preset.acid] + ": " + formatEU(got.massAcid) + " g",
        n[preset.base] + ": " + formatEU(got.massBase) + " g"
      ];
      if (preset.densityAcid) {
        lines.push(t.aceticMl + ": " + formatEU(got.massAcid / preset.densityAcid) + " mL");
      }
      show(out, { value: "pH " + formatEU(pH), lines: lines, warn: got.far ? t.far : "", note: t.pairNote });
    });
  }

  function buildAcid(form, out, pack) {
    var t = pack.t;
    var n = pack.n;
    var bottle = select("lx-acid", "acid", [{ id: "custom" }].concat(ACIDS).map(function (a) { return [a.id, n[a.id]]; }));
    form.appendChild(field(t.bottle, bottle));
    form.appendChild(field(t.pct, inline(input("lx-ap", "pct", "37"), el("span", { class: "lx-calc__unit", text: "% (w/w)" }))));
    form.appendChild(field(t.density, inline(input("lx-ad", "density", "1,19"), el("span", { class: "lx-calc__unit", text: "g/mL" }))));
    form.appendChild(field(t.mw, inline(input("lx-amw", "mw", "36,46"), el("span", { class: "lx-calc__unit", text: "g/mol" }))));
    form.appendChild(field(t.protons, input("lx-an", "protons", "1")));
    form.appendChild(el("p", { class: "lx-calc__note", text: t.acidNote }));
    form.appendChild(el("p", { class: "lx-calc__note", text: t.acidSafe }));
    form.appendChild(field(t.target, inline(input("lx-at", "target", ""), select("lx-atu", "tu", MOL))));
    form.appendChild(field(t.finalVol, inline(input("lx-af", "final", ""), select("lx-afu", "fu", VOLU))));
    form.querySelector("#lx-afu").value = "mL";
    form.appendChild(buttons(t));
    form.appendChild(out);
    function apply() {
      var row = ACIDS.filter(function (a) { return a.id === bottle.value; })[0];
      if (!row) return;
      form.elements.pct.value = formatEU(row.pct);
      form.elements.density.value = formatEU(row.d);
      form.elements.mw.value = formatEU(row.mw);
      form.elements.protons.value = formatEU(row.n);
    }
    bottle.addEventListener("change", apply);
    bottle.value = "hcl";
    apply();
    form.addEventListener("reset", function () {
      window.setTimeout(function () {
        bottle.value = "hcl";
        apply();
        out.textContent = "";
      }, 0);
    });
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var pct = parseNum(val(form, "pct"));
      var density = parseNum(val(form, "density"));
      var mw = parseNum(val(form, "mw"));
      var protons = parseNum(val(form, "protons"));
      if (pct == null || pct <= 0 || pct > 100) return show(out, { error: t.errPct });
      if (!pos(density) || !pos(mw) || !pos(protons)) return show(out, { error: t.errNum });
      var M = molarityOfBottle(pct, density, mw);
      var lines = [
        t.molarity + ": " + formatEU(M) + " mol/L",
        t.normal + ": " + formatEU(M * protons) + " N",
        "(" + formatEU(pct) + " × " + formatEU(density) + " × 10) / " + formatEU(mw) + " = " + formatEU(M),
        t.normality
      ];
      var target = parseNum(val(form, "target"));
      var finalV = parseNum(val(form, "final"));
      var warn = "";
      if (target != null || finalV != null) {
        if (!pos(target) || !pos(finalV)) return show(out, { error: t.errNum });
        var targetM = target * CONC[val(form, "tu")].to;
        if (targetM > M) return show(out, { error: t.errWeak });
        var litres = toLitres(finalV, val(form, "fu"));
        var stockL = targetM * litres / M;
        lines.push(t.stockTake + ": " + formatEU(fromLitres(stockL, val(form, "fu"))) + " " + val(form, "fu"));
        lines.push(t.diluent + ": " + formatEU(fromLitres(litres - stockL, val(form, "fu"))) + " " + val(form, "fu"));
        lines.push(t.acidSafe);
      }
      show(out, { value: formatEU(M) + " mol/L", lines: lines, warn: warn });
    });
  }

  function buildSerial(form, out, pack) {
    var t = pack.t;
    form.appendChild(field(t.c1, inline(input("lx-sc", "c0", "1"), select("lx-scu", "cu", DIL))));
    form.appendChild(field(t.factor, input("lx-sf", "factor", "10")));
    form.appendChild(field(t.steps, input("lx-ss", "steps", "6")));
    form.appendChild(field(t.tube, inline(input("lx-st", "tube", "1"), select("lx-stu", "tu", VOLU))));
    form.querySelector("#lx-stu").value = "mL";
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var c0 = parseNum(val(form, "c0"));
      var factor = parseNum(val(form, "factor"));
      var steps = parseNum(val(form, "steps"));
      var tube = parseNum(val(form, "tube"));
      if (!pos(c0) || !pos(tube)) return show(out, { error: t.errNum });
      if (!pos(factor) || factor <= 1) return show(out, { error: t.errFactor });
      if (steps == null || steps < 1 || steps > 12 || Math.round(steps) !== steps) return show(out, { error: t.errSteps });
      var got = serialRows(c0, factor, steps, tube);
      var unit = val(form, "cu") === "% w/v" ? "% (w/v)" : val(form, "cu");
      var vu = val(form, "tu");
      var rows = [[t.stockRow, formatEU(c0) + " " + unit, "—", "—"]];
      got.rows.forEach(function (row) {
        rows.push([String(row.step), formatEU(row.conc) + " " + unit, formatEU(row.transfer) + " " + vu, formatEU(row.diluent) + " " + vu]);
      });
      show(out, {
        value: formatEU(factor) + "×",
        lines: [t.transfer + " " + formatEU(got.transfer) + " " + vu + ", " + t.diluentCol.toLowerCase() + " " + formatEU(got.diluent) + " " + vu],
        table: { head: [t.step, t.conc, t.transfer, t.diluentCol], rows: rows }
      });
    });
  }

  function buildUnits(form, out, pack) {
    var t = pack.t;
    form.appendChild(field(t.value, inline(input("lx-uv", "value", "1"), select("lx-uf", "from", ALLU))));
    form.appendChild(field(t.to, select("lx-ut", "to", ALLU)));
    form.querySelector("#lx-ut").value = "g/L";
    form.appendChild(field(t.mw, inline(input("lx-umw", "mw", "58,44"), el("span", { class: "lx-calc__unit", text: "g/mol" }))));
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var value = parseNum(val(form, "value"));
      var mw = parseNum(val(form, "mw"));
      var from = val(form, "from");
      var to = val(form, "to");
      if (!pos(value) && value !== 0) return show(out, { error: t.errNum });
      if (value < 0) return show(out, { error: t.errNum });
      var cross = CONC[from].dim !== CONC[to].dim;
      if (cross && !pos(mw)) return show(out, { error: t.errMw });
      var g = toGramsPerL(value, from, mw);
      if (g == null) return show(out, { error: t.errMw });
      var outV = fromGramsPerL(g, to, mw);
      var lines = [];
      if (from === "ppm" || to === "ppm") lines.push(t.ppm);
      if (cross) lines.push(formatEU(value) + " " + from + " × " + formatEU(mw) + " g/mol");
      var label = to === "% w/v" ? "% (w/v)" : to;
      show(out, { value: formatEU(outV) + " " + label, lines: lines });
    });
  }

  var PBS_PARTS = [
    { id: "nacl", gPerL: 8.00628 },
    { id: "kcl", gPerL: 0.201285 },
    { id: "na2hpo4", gPerL: 1.4196 },
    { id: "kh2po4", gPerL: 0.244962 }
  ];
  var TBS_PARTS = [
    { id: "tris", gPerL: 6.057 },
    { id: "nacl", gPerL: 8.766 }
  ];

  function scaled(parts, fold) {
    return parts.map(function (row) {
      return { id: row.id, gPerL: row.gPerL * fold, kind: "mass" };
    });
  }

  var RECIPES = {
    pbs1: { parts: scaled(PBS_PARTS, 1), note: "ph" },
    pbs10: { parts: scaled(PBS_PARTS, 10), note: "ph" },
    tbs1: { parts: scaled(TBS_PARTS, 1), note: "tbs" },
    tbs10: { parts: scaled(TBS_PARTS, 10), note: "tbs" },
    tbst: { parts: scaled(TBS_PARTS, 1).concat([{ id: "tween", mLPerL: 1, kind: "vol" }]), note: "tbs" },
    pbst: { parts: scaled(PBS_PARTS, 1).concat([{ id: "tween", mLPerL: 1, kind: "vol" }]), note: "ph" },
    nfdm: { percent: 5, solute: "milk", diluent: "TBST" },
    bsa: { percent: 5, solute: "bsa", diluent: "TBST" },
    sds: {
      parts: [
        { id: "tris1m", mLPerL: 200, kind: "vol" },
        { id: "sdsSalt", gPerL: 80, kind: "mass" },
        { id: "glycerol", mLPerL: 400, kind: "vol" },
        { id: "bpb", gPerL: 0.2, kind: "mass" },
        { id: "bme", mLPerL: 100, kind: "vol", fresh: true }
      ],
      note: "sds"
    },
    stripAcid: {
      parts: [
        { id: "glycine", gPerL: 15, kind: "mass" },
        { id: "sdsSalt", gPerL: 1, kind: "mass" },
        { id: "tween", mLPerL: 10, kind: "vol" }
      ],
      note: "acidStrip"
    },
    stripDet: {
      parts: [
        { id: "sds10", mLPerL: 200, kind: "vol" },
        { id: "tris05", mLPerL: 125, kind: "vol" },
        { id: "bme", mLPerL: 8, kind: "vol", fresh: true }
      ],
      note: "sds"
    },
    stripStocks: {
      parts: [
        { id: "sdsSalt", gPerL: 100, kind: "mass", line: "10%" },
        { id: "tris-hcl", gPerL: 78.8, kind: "mass", line: "0,5 mol/L" },
        { id: "glycine", gPerL: 75.07, kind: "mass", line: "1 mol/L" }
      ],
      note: "stocks"
    }
  };

  var ANTOINE = [
    { id: "water", A: 8.07131, B: 1730.63, C: 233.426, tMin: 1, tMax: 100 },
    { id: "ethanol", A: 8.20417, B: 1642.89, C: 230.3, tMin: -57, tMax: 80 },
    { id: "methanol", A: 7.8975, B: 1474.08, C: 229.13, tMin: -20, tMax: 140 },
    { id: "acetone", A: 7.2316, B: 1277.03, C: 237.23, tMin: -32, tMax: 77 },
    { id: "hexane", A: 6.87601, B: 1171.17, C: 224.408, tMin: -25, tMax: 69 },
    { id: "toluene", A: 6.95464, B: 1344.8, C: 219.482, tMin: 6, tMax: 137 },
    { id: "ethylacetate", A: 7.0146, B: 1211.9, C: 216.01, tMin: -13, tMax: 112 },
    { id: "benzene", A: 6.90565, B: 1211.033, C: 220.79, tMin: 8, tMax: 80 }
  ];

  var MESH = [
    [4, 4750], [5, 4000], [6, 3350], [7, 2800], [8, 2360], [10, 2000], [12, 1700], [14, 1400],
    [16, 1180], [18, 1000], [20, 850], [25, 710], [30, 600], [35, 500], [40, 425], [45, 355],
    [50, 300], [60, 250], [70, 212], [80, 180], [100, 150], [120, 125], [140, 106], [170, 90],
    [200, 75], [230, 63], [270, 53], [325, 45], [400, 38]
  ];

  var SOLUBILITY = [
    { id: "nacl", pts: [[0, 35.7], [20, 35.9], [100, 39.2]] },
    { id: "kcl", pts: [[0, 28.0], [20, 34.2], [100, 56.3]] },
    { id: "sucrose", pts: [[0, 179], [20, 204], [100, 487]] },
    { id: "kno3", pts: [[0, 13.3], [20, 31.6], [100, 246]] }
  ];

  var DYES = [
    ["DAPI", 358, 461], ["Hoechst 33342", 350, 461], ["Fluorescein", 494, 512], ["FITC", 495, 519],
    ["TRITC", 544, 572], ["Texas Red", 595, 615], ["Cy3", 550, 570], ["Cy5", 649, 670],
    ["Ethidium bromide, with DNA", 522, 605], ["Propidium iodide", 535, 617], ["SYBR Green I", 497, 520],
    ["GFP", 488, 507], ["mCherry", 587, 610], ["Rhodamine B", 543, 565]
  ];

  var MEASURE = {
    length: [["m", 1], ["cm", 0.01], ["mm", 0.001], ["µm", 1e-6], ["nm", 1e-9], ["Å", 1e-10], ["in", 0.0254], ["ft", 0.3048]],
    mass: [["kg", 1], ["g", 0.001], ["mg", 1e-6], ["µg", 1e-9], ["ng", 1e-12], ["lb", 0.45359237], ["oz", 0.028349523]],
    volume: [["L", 1], ["mL", 0.001], ["µL", 1e-6], ["nL", 1e-9], ["fl oz", 0.0295735], ["US gal", 3.7854118]],
    pressure: [["Pa", 1], ["kPa", 1000], ["bar", 100000], ["mbar", 100], ["atm", 101325], ["psi", 6894.757], ["torr", 133.322], ["mmHg", 133.322]],
    density: [["g/mL", 1000], ["kg/m³", 1], ["g/L", 1], ["lb/ft³", 16.01846]],
    flow: [["mL/min", 1], ["µL/min", 0.001], ["L/min", 1000], ["L/h", 16.6666667], ["m³/h", 16666.6667]],
    torque: [["N·m", 1], ["mN·m", 0.001], ["lbf·in", 0.1129848], ["lbf·ft", 1.355818], ["kgf·cm", 0.0980665]]
  };

  function rcfFromRpm(rCm, rpm) { return 1.118e-5 * rCm * rpm * rpm; }
  function rpmFromRcf(rCm, rcf) { return Math.sqrt(rcf / (1.118e-5 * rCm)); }
  function antoineP(A, B, C, tC) { return Math.pow(10, A - B / (tC + C)); }
  function antoineT(A, B, C, pMmHg) { return B / (A - Math.log10(pMmHg)) - C; }
  function aFromPercentT(pct) { return 2 - Math.log10(pct); }
  function percentTFromA(a) { return 100 * Math.pow(10, -a); }
  function awgMm(n) { return 0.127 * Math.pow(92, (36 - n) / 39); }
  function hplcTransfer(L1, d1, dp1, F1, V1, t1, L2, d2, dp2) {
    var area = Math.pow(d2 / d1, 2);
    var F2 = F1 * area * (dp1 / dp2);
    return {
      F2: F2,
      V2: V1 * area * (L2 / L1),
      t2: t1 * (L2 / L1) * (dp2 / dp1)
    };
  }
  function solubilityAt(pts, t) {
    var edge = t < pts[0][0] || t > pts[pts.length - 1][0];
    var lo = pts[0];
    var hi = pts[pts.length - 1];
    if (t <= lo[0]) return { g: lo[1], edge: t !== lo[0] };
    if (t >= hi[0]) return { g: hi[1], edge: t !== hi[0] };
    for (var i = 1; i < pts.length; i++) {
      if (t <= pts[i][0]) {
        var f = (t - pts[i - 1][0]) / (pts[i][0] - pts[i - 1][0]);
        return { g: pts[i - 1][1] + f * (pts[i][1] - pts[i - 1][1]), edge: false };
      }
    }
    return { g: hi[1], edge: edge };
  }

  function litresOf(form) {
    var vol = parseNum(val(form, "vol"));
    if (!pos(vol)) return null;
    return toLitres(vol, val(form, "vu"));
  }

  function buildRecipe(form, out, pack, id) {
    var t = pack.t;
    var n = pack.n;
    var recipe = RECIPES[id];
    form.appendChild(field(t.vol, inline(input("lx-rv", "vol", "1"), select("lx-rvu", "vu", VOLU))));
    if (recipe.percent) {
      form.appendChild(field(t.percent, inline(input("lx-rp", "pct", String(recipe.percent).replace(".", ",")), el("span", { class: "lx-calc__unit", text: "%" }))));
    }
    form.appendChild(buttons(t));
    form.appendChild(out);
    var notes = {
      ph: t.phAdjust + " 1× lands near pH 7,4. Anhydrous salts.",
      tbs: t.phAdjust + " pH 7,6.",
      sds: t.waterFill + " " + t.addFresh,
      acidStrip: t.phAdjust + " pH 2,2.",
      stocks: t.phAdjust
    };
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var litres = litresOf(form);
      if (litres == null) return show(out, { error: t.errNum });
      if (recipe.percent) {
        var pct = parseNum(val(form, "pct"));
        if (pct == null || pct <= 0 || pct > 100) return show(out, { error: t.errPct });
        var grams = pct / 100 * litres * 1000;
        show(out, {
          value: formatEU(grams) + " g",
          lines: [n[recipe.solute] + " in " + formatEU(litres * 1000) + " mL " + recipe.diluent],
          note: t.weigh
        });
        return;
      }
      var lines = recipe.parts.map(function (part) {
        var amount = part.kind === "vol" ? part.mLPerL * litres : part.gPerL * litres;
        var unit = part.kind === "vol" ? "mL" : "g";
        var label = n[part.id] || part.id;
        if (part.line) label += " (" + part.line + ")";
        if (part.fresh) label += " — " + t.addFresh;
        return label + ": " + formatEU(amount) + " " + unit;
      });
      show(out, { value: formatEU(litres) + " L", lines: lines, note: notes[recipe.note] || "" });
    });
  }

  function buildGforce(form, out, pack) {
    var t = pack.t;
    var solve = select("lx-gs", "solve", [["rcf", t.rcf], ["rpm", t.rpm]]);
    form.appendChild(field(t.solveFor, solve));
    form.appendChild(field(t.radius, inline(input("lx-gr", "radius", "10"), select("lx-gru", "ru", [["cm", "cm"], ["mm", "mm"]]))));
    form.appendChild(field(t.rpm, input("lx-grpm", "rpm", "3.000")));
    form.appendChild(field(t.rcf, inline(input("lx-grcf", "rcf", ""), el("span", { class: "lx-calc__unit", text: "× g" }))));
    form.appendChild(buttons(t));
    form.appendChild(out);
    function sync() { form.querySelector("#lx-grpm").closest(".lx-calc__field").hidden = solve.value === "rpm"; form.querySelector("#lx-grcf").closest(".lx-calc__field").hidden = solve.value === "rcf"; }
    solve.addEventListener("change", sync);
    sync();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var radius = parseNum(val(form, "radius"));
      if (!pos(radius)) return show(out, { error: t.errNum });
      var rCm = val(form, "ru") === "mm" ? radius / 10 : radius;
      if (solve.value === "rcf") {
        var rpm = parseNum(val(form, "rpm"));
        if (!pos(rpm)) return show(out, { error: t.errNum });
        show(out, { value: formatEU(rcfFromRpm(rCm, rpm)) + " × g", lines: ["1,118 × 10⁻⁵ × r × rpm²"] });
      } else {
        var rcf = parseNum(val(form, "rcf"));
        if (!pos(rcf)) return show(out, { error: t.errNum });
        show(out, { value: formatEU(rpmFromRcf(rCm, rcf)) + " rpm", lines: ["1,118 × 10⁻⁵ × r × rpm²"] });
      }
    });
  }

  function buildPcr(form, out, pack) {
    var t = pack.t;
    var n = pack.n;
    form.appendChild(field(t.reactions, input("lx-pn", "n", "10")));
    form.appendChild(field(t.overage, inline(input("lx-po", "over", "10"), el("span", { class: "lx-calc__unit", text: "%" }))));
    form.appendChild(field(t.vol, inline(input("lx-pv", "rx", "25"), el("span", { class: "lx-calc__unit", text: "µL" }))));
    var rows = [
      ["mix", "2× mix", "12,5"],
      ["fwd", n.loc ? "" : "Primer, forward", "0,5"],
      ["rev", "Primer, reverse", "0,5"],
      ["tpl", "Template", "1"],
      ["water", t.water, "10,5"]
    ];
    var labels = { en: ["2× mix", "Primer, forward", "Primer, reverse", "Template", "Water"], nl: ["2× mix", "Primer, vooruit", "Primer, terug", "Matrijs", "Water"], fr: ["Mix 2×", "Amorce sens", "Amorce antisens", "Matrice", "Eau"] };
    var names = labels[pack.loc] || labels.en;
    rows.forEach(function (row, i) {
      form.appendChild(field(names[i], inline(input("lx-pc" + i, "c" + i, row[2]), el("span", { class: "lx-calc__unit", text: "µL" }))));
    });
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var count = parseNum(val(form, "n"));
      var over = parseNum(val(form, "over"));
      var rx = parseNum(val(form, "rx"));
      if (!pos(count) || over == null || over < 0 || !pos(rx)) return show(out, { error: t.errNum });
      var factor = count * (1 + over / 100);
      var lines = [];
      var sum = 0;
      for (var i = 0; i < rows.length; i++) {
        var each = parseNum(val(form, "c" + i));
        if (each == null || each < 0) return show(out, { error: t.errNum });
        sum += each;
        lines.push(names[i] + ": " + formatEU(each * factor) + " µL");
      }
      var warn = Math.abs(sum - rx) > 0.05 ? formatEU(sum) + " µL / " + formatEU(rx) + " µL" : "";
      show(out, { value: formatEU(factor) + " ×", lines: lines, warn: warn });
    });
  }

  function buildDyes(form, out, pack) {
    var t = pack.t;
    var box = el("input", { id: "lx-dye", class: "lx-calc__input", placeholder: t.dyeFilter });
    form.appendChild(field(t.dyeFilter, box));
    var table = el("table");
    form.appendChild(table);
    function draw() {
      var q = box.value.toLowerCase();
      table.textContent = "";
      var head = el("tr");
      [t.compound, t.ex + " (nm)", t.em + " (nm)"].forEach(function (h) { head.appendChild(el("th", { text: h })); });
      table.appendChild(head);
      DYES.forEach(function (row) {
        if (q && row[0].toLowerCase().indexOf(q) === -1) return;
        var tr = el("tr");
        tr.appendChild(el("td", { text: row[0] }));
        tr.appendChild(el("td", { text: String(row[1]) }));
        tr.appendChild(el("td", { text: String(row[2]) }));
        table.appendChild(tr);
      });
    }
    box.addEventListener("input", draw);
    draw();
  }

  function buildAbsorbance(form, out, pack) {
    var t = pack.t;
    var solve = select("lx-as", "solve", [["a", t.absorbance], ["t", t.transmittance]]);
    form.appendChild(field(t.solveFor, solve));
    form.appendChild(field(t.transmittance, inline(input("lx-atp", "pct", "50"), el("span", { class: "lx-calc__unit", text: "%" }))));
    form.appendChild(field(t.absorbance, input("lx-aa", "a", "0,301")));
    form.appendChild(buttons(t));
    form.appendChild(out);
    function sync() {
      form.querySelector("#lx-atp").closest(".lx-calc__field").hidden = solve.value === "t";
      form.querySelector("#lx-aa").closest(".lx-calc__field").hidden = solve.value === "a";
    }
    solve.addEventListener("change", sync);
    sync();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      if (solve.value === "a") {
        var pct = parseNum(val(form, "pct"));
        if (pct == null || pct <= 0 || pct > 100) return show(out, { error: t.errPct });
        show(out, { value: formatEU(aFromPercentT(pct)), lines: ["A = 2 − log10(%T)"] });
      } else {
        var a = parseNum(val(form, "a"));
        if (a == null || a < 0) return show(out, { error: t.errNum });
        show(out, { value: formatEU(percentTFromA(a)) + " %", lines: ["%T = 100 × 10^(−A)"] });
      }
    });
    var table = el("table");
    var head = el("tr");
    ["%T", "A"].forEach(function (h) { head.appendChild(el("th", { text: h })); });
    table.appendChild(head);
    [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 5, 1, 0.1].forEach(function (pct) {
      var tr = el("tr");
      tr.appendChild(el("td", { text: formatEU(pct) }));
      tr.appendChild(el("td", { text: formatEU(aFromPercentT(pct)) }));
      table.appendChild(tr);
    });
    form.appendChild(table);
  }

  function buildMeasure(form, out, pack, preset) {
    var t = pack.t;
    var presetKind = { length: "length", temp: "temp", density: "density", massw: "mass", flow: "flow", pressure: "pressure", volumew: "volume", torque: "torque" }[preset] || null;
    var kinds = [["length", "Length"], ["mass", "Mass"], ["volume", "Volume"], ["temp", "Temperature"], ["pressure", "Pressure"], ["density", "Density"], ["flow", "Flow"], ["torque", "Torque"]];
    if (pack.loc === "nl") kinds = [["length", "Lengte"], ["mass", "Massa"], ["volume", "Volume"], ["temp", "Temperatuur"], ["pressure", "Druk"], ["density", "Dichtheid"], ["flow", "Debiet"], ["torque", "Koppel"]];
    if (pack.loc === "fr") kinds = [["length", "Longueur"], ["mass", "Masse"], ["volume", "Volume"], ["temp", "Température"], ["pressure", "Pression"], ["density", "Masse volumique"], ["flow", "Débit"], ["torque", "Couple"]];
    var kind = select("lx-mk", "kind", kinds);
    var kindField = field(t.kindOf, kind);
    form.appendChild(kindField);
    if (presetKind) {
      kind.value = presetKind;
      kindField.hidden = true;
    }
    var from = select("lx-mf", "from", MEASURE.length);
    var to = select("lx-mt", "to", MEASURE.length);
    form.appendChild(field(t.value, inline(input("lx-mv", "value", "1"), from)));
    form.appendChild(field(t.to, to));
    form.appendChild(buttons(t));
    form.appendChild(out);
    function fill() {
      var spec = kind.value === "temp" ? [["°C", "°C"], ["°F", "°F"], ["K", "K"]] : MEASURE[kind.value];
      from.textContent = "";
      to.textContent = "";
      spec.forEach(function (opt) {
        from.appendChild(el("option", { value: opt[0], text: opt[0] }));
        to.appendChild(el("option", { value: opt[0], text: opt[0] }));
      });
      if (spec[1]) to.value = spec[1][0];
    }
    kind.addEventListener("change", fill);
    fill();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var value = parseNum(val(form, "value"));
      if (value == null) return show(out, { error: t.errNum });
      var src = val(form, "from");
      var dst = val(form, "to");
      var result;
      if (kind.value === "temp") {
        var c = src === "°C" ? value : src === "°F" ? (value - 32) * 5 / 9 : value - 273.15;
        result = dst === "°C" ? c : dst === "°F" ? c * 9 / 5 + 32 : c + 273.15;
      } else {
        var table = {};
        MEASURE[kind.value].forEach(function (opt) { table[opt[0]] = opt[1]; });
        result = value * table[src] / table[dst];
      }
      show(out, { value: formatEU(result) + " " + dst });
    });
  }

  function buildHplc(form, out, pack) {
    var t = pack.t;
    function pair(label, a, b, unit) {
      var wrap = el("div", { class: "lx-calc__field" });
      wrap.appendChild(el("label", { text: label }));
      var row = el("div", { class: "lx-calc__inline" });
      row.appendChild(input("lx-" + a, a, ""));
      row.appendChild(input("lx-" + b, b, ""));
      wrap.appendChild(row);
      if (unit) wrap.appendChild(el("span", { class: "lx-calc__unit", text: unit }));
      return wrap;
    }
    form.appendChild(el("p", { class: "lx-calc__note", text: t.fromCol + "  /  " + t.toCol }));
    form.appendChild(pair(t.columnLength + " (mm)", "l1", "l2", "1 → 2"));
    form.appendChild(pair(t.diameter + " (mm)", "d1", "d2"));
    form.appendChild(pair(t.particle + " (µm)", "p1", "p2"));
    form.appendChild(field(t.flow, inline(input("lx-hf", "f1", "1"), el("span", { class: "lx-calc__unit", text: "mL/min" }))));
    form.appendChild(field(t.injection, inline(input("lx-hv", "v1", "10"), el("span", { class: "lx-calc__unit", text: "µL" }))));
    form.appendChild(field(t.gradient, inline(input("lx-ht", "t1", "10"), el("span", { class: "lx-calc__unit", text: "min" }))));
    form.querySelector("#lx-l1").value = "150";
    form.querySelector("#lx-l2").value = "150";
    form.querySelector("#lx-d1").value = "4,6";
    form.querySelector("#lx-d2").value = "2,1";
    form.querySelector("#lx-p1").value = "5";
    form.querySelector("#lx-p2").value = "5";
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var nums = ["l1", "l2", "d1", "d2", "p1", "p2", "f1", "v1", "t1"].map(function (name) { return parseNum(val(form, name)); });
      if (nums.some(function (n) { return !pos(n); })) return show(out, { error: t.errNum });
      var got = hplcTransfer(nums[0], nums[2], nums[4], nums[6], nums[7], nums[8], nums[1], nums[3], nums[5]);
      show(out, {
        value: formatEU(got.F2) + " mL/min",
        lines: [t.injection + ": " + formatEU(got.V2) + " µL", t.gradient + ": " + formatEU(got.t2) + " min"],
        note: "F2 = F1 × (d2/d1)² × (dp1/dp2)"
      });
    });
  }

  function buildMesh(form, out, pack) {
    var t = pack.t;
    var mesh = select("lx-mesh", "mesh", MESH.map(function (row) { return [String(row[0]), String(row[0])]; }));
    form.appendChild(field(t.mesh, mesh));
    form.appendChild(field(t.opening, inline(input("lx-open", "open", ""), el("span", { class: "lx-calc__unit", text: "µm" }))));
    form.appendChild(buttons(t));
    form.appendChild(out);
    mesh.addEventListener("change", function () {
      var row = MESH.filter(function (item) { return String(item[0]) === mesh.value; })[0];
      form.elements.open.value = formatEU(row[1]);
    });
    form.elements.open.value = formatEU(MESH[0][1]);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var opening = parseNum(val(form, "open"));
      var byMesh = MESH.filter(function (item) { return String(item[0]) === mesh.value; })[0];
      if (opening == null) opening = byMesh[1];
      var nearest = MESH[0];
      MESH.forEach(function (item) {
        if (Math.abs(item[1] - opening) < Math.abs(nearest[1] - opening)) nearest = item;
      });
      show(out, { value: nearest[0] + " mesh", lines: [formatEU(nearest[1]) + " µm", t.nearest], note: "ASTM E11, nominal opening" });
    });
  }

  function buildWire(form, out, pack) {
    var t = pack.t;
    form.appendChild(field(t.gauge, input("lx-awg", "awg", "20")));
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var n = parseNum(val(form, "awg"));
      if (n == null || n < 0 || n > 40) return show(out, { error: t.errNum });
      var mm = awgMm(n);
      show(out, { value: formatEU(mm) + " mm", lines: [formatEU(mm / 25.4) + " in"], note: "d = 0,127 × 92^((36−n)/39) mm" });
    });
  }

  function buildAntoine(form, out, pack) {
    var t = pack.t;
    var names = {
      en: { water: "Water", ethanol: "Ethanol", methanol: "Methanol", acetone: "Acetone", hexane: "Hexane", toluene: "Toluene", ethylacetate: "Ethyl acetate", benzene: "Benzene" },
      nl: { water: "Water", ethanol: "Ethanol", methanol: "Methanol", acetone: "Aceton", hexane: "Hexaan", toluene: "Tolueen", ethylacetate: "Ethylacetaat", benzene: "Benzeen" },
      fr: { water: "Eau", ethanol: "Éthanol", methanol: "Méthanol", acetone: "Acétone", hexane: "Hexane", toluene: "Toluène", ethylacetate: "Acétate d'éthyle", benzene: "Benzène" }
    }[pack.loc];
    var solve = select("lx-ans", "solve", [["p", t.pressure], ["t", t.temperature]]);
    var solvent = select("lx-solv", "solvent", ANTOINE.map(function (row) { return [row.id, names[row.id]]; }));
    form.appendChild(field(t.solveFor, solve));
    form.appendChild(field(t.compound, solvent));
    form.appendChild(field(t.temperature, inline(input("lx-ant", "temp", "25"), el("span", { class: "lx-calc__unit", text: "°C" }))));
    form.appendChild(field(t.pressure, inline(input("lx-anp", "press", "760"), el("span", { class: "lx-calc__unit", text: "mmHg" }))));
    form.appendChild(buttons(t));
    form.appendChild(out);
    function sync() {
      form.querySelector("#lx-ant").closest(".lx-calc__field").hidden = solve.value === "t";
      form.querySelector("#lx-anp").closest(".lx-calc__field").hidden = solve.value === "p";
    }
    solve.addEventListener("change", sync);
    sync();
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var row = ANTOINE.filter(function (item) { return item.id === solvent.value; })[0];
      if (solve.value === "p") {
        var temp = parseNum(val(form, "temp"));
        if (temp == null) return show(out, { error: t.errNum });
        var p = antoineP(row.A, row.B, row.C, temp);
        show(out, {
          value: formatEU(p) + " mmHg",
          lines: [formatEU(p * 0.00133322) + " bar"],
          warn: temp < row.tMin || temp > row.tMax ? t.rangeWarn : "",
          note: "log10(P) = A − B/(T + C). P in mmHg, T in °C. " + formatEU(row.tMin) + "–" + formatEU(row.tMax) + " °C."
        });
      } else {
        var press = parseNum(val(form, "press"));
        if (!pos(press)) return show(out, { error: t.errNum });
        var temp2 = antoineT(row.A, row.B, row.C, press);
        show(out, {
          value: formatEU(temp2) + " °C",
          warn: temp2 < row.tMin || temp2 > row.tMax ? t.rangeWarn : "",
          note: "log10(P) = A − B/(T + C)"
        });
      }
    });
  }

  function buildSolubility(form, out, pack) {
    var t = pack.t;
    var n = pack.n;
    var compound = select("lx-sol", "compound", SOLUBILITY.map(function (row) { return [row.id, n[row.id]]; }));
    form.appendChild(field(t.compound, compound));
    form.appendChild(field(t.temperature, inline(input("lx-solt", "temp", "20"), el("span", { class: "lx-calc__unit", text: "°C" }))));
    form.appendChild(buttons(t));
    form.appendChild(out);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var temp = parseNum(val(form, "temp"));
      if (temp == null) return show(out, { error: t.errNum });
      var row = SOLUBILITY.filter(function (item) { return item.id === compound.value; })[0];
      var got = solubilityAt(row.pts, temp);
      show(out, {
        value: formatEU(got.g) + " g / 100 g",
        lines: [n[row.id]],
        warn: got.edge ? t.rangeWarn : "",
        note: "Grams per 100 g of water. Between the table points the line is straight."
      });
    });
  }

  var PT = [
    ["H", 1.008, 0], ["He", 4.0026, 0], ["Li", 6.94, 0], ["Be", 9.0122, 0], ["B", 10.81, 0], ["C", 12.011, 0], ["N", 14.007, 0], ["O", 15.999, 0], ["F", 18.998, 0], ["Ne", 20.18, 0],
    ["Na", 22.99, 0], ["Mg", 24.305, 0], ["Al", 26.982, 0], ["Si", 28.085, 0], ["P", 30.974, 0], ["S", 32.06, 0], ["Cl", 35.45, 0], ["Ar", 39.95, 0],
    ["K", 39.098, 0], ["Ca", 40.078, 0], ["Sc", 44.956, 0], ["Ti", 47.867, 0], ["V", 50.942, 0], ["Cr", 51.996, 0], ["Mn", 54.938, 0], ["Fe", 55.845, 0], ["Co", 58.933, 0], ["Ni", 58.693, 0], ["Cu", 63.546, 0], ["Zn", 65.38, 0], ["Ga", 69.723, 0], ["Ge", 72.63, 0], ["As", 74.922, 0], ["Se", 78.971, 0], ["Br", 79.904, 0], ["Kr", 83.798, 0],
    ["Rb", 85.468, 0], ["Sr", 87.62, 0], ["Y", 88.906, 0], ["Zr", 91.224, 0], ["Nb", 92.906, 0], ["Mo", 95.95, 0], ["Tc", 98, 1], ["Ru", 101.07, 0], ["Rh", 102.91, 0], ["Pd", 106.42, 0], ["Ag", 107.87, 0], ["Cd", 112.41, 0], ["In", 114.82, 0], ["Sn", 118.71, 0], ["Sb", 121.76, 0], ["Te", 127.6, 0], ["I", 126.9, 0], ["Xe", 131.29, 0],
    ["Cs", 132.91, 0], ["Ba", 137.33, 0], ["La", 138.91, 0], ["Ce", 140.12, 0], ["Pr", 140.91, 0], ["Nd", 144.24, 0], ["Pm", 145, 1], ["Sm", 150.36, 0], ["Eu", 151.96, 0], ["Gd", 157.25, 0], ["Tb", 158.93, 0], ["Dy", 162.5, 0], ["Ho", 164.93, 0], ["Er", 167.26, 0], ["Tm", 168.93, 0], ["Yb", 173.05, 0], ["Lu", 174.97, 0],
    ["Hf", 178.49, 0], ["Ta", 180.95, 0], ["W", 183.84, 0], ["Re", 186.21, 0], ["Os", 190.23, 0], ["Ir", 192.22, 0], ["Pt", 195.08, 0], ["Au", 196.97, 0], ["Hg", 200.59, 0], ["Tl", 204.38, 0], ["Pb", 207.2, 0], ["Bi", 208.98, 0], ["Po", 209, 1], ["At", 210, 1], ["Rn", 222, 1],
    ["Fr", 223, 1], ["Ra", 226, 1], ["Ac", 227, 1], ["Th", 232.04, 0], ["Pa", 231.04, 0], ["U", 238.03, 0], ["Np", 237, 1], ["Pu", 244, 1], ["Am", 243, 1], ["Cm", 247, 1], ["Bk", 247, 1], ["Cf", 251, 1], ["Es", 252, 1], ["Fm", 257, 1], ["Md", 258, 1], ["No", 259, 1], ["Lr", 266, 1],
    ["Rf", 267, 1], ["Db", 268, 1], ["Sg", 269, 1], ["Bh", 270, 1], ["Hs", 269, 1], ["Mt", 278, 1], ["Ds", 281, 1], ["Rg", 282, 1], ["Cn", 285, 1], ["Nh", 286, 1], ["Fl", 289, 1], ["Mc", 290, 1], ["Lv", 293, 1], ["Ts", 294, 1], ["Og", 294, 1]
  ];
  var PT_EN = "Hydrogen Helium Lithium Beryllium Boron Carbon Nitrogen Oxygen Fluorine Neon Sodium Magnesium Aluminium Silicon Phosphorus Sulfur Chlorine Argon Potassium Calcium Scandium Titanium Vanadium Chromium Manganese Iron Cobalt Nickel Copper Zinc Gallium Germanium Arsenic Selenium Bromine Krypton Rubidium Strontium Yttrium Zirconium Niobium Molybdenum Technetium Ruthenium Rhodium Palladium Silver Cadmium Indium Tin Antimony Tellurium Iodine Xenon Caesium Barium Lanthanum Cerium Praseodymium Neodymium Promethium Samarium Europium Gadolinium Terbium Dysprosium Holmium Erbium Thulium Ytterbium Lutetium Hafnium Tantalum Tungsten Rhenium Osmium Iridium Platinum Gold Mercury Thallium Lead Bismuth Polonium Astatine Radon Francium Radium Actinium Thorium Protactinium Uranium Neptunium Plutonium Americium Curium Berkelium Californium Einsteinium Fermium Mendelevium Nobelium Lawrencium Rutherfordium Dubnium Seaborgium Bohrium Hassium Meitnerium Darmstadtium Roentgenium Copernicium Nihonium Flerovium Moscovium Livermorium Tennessine Oganesson".split(" ");
  var PT_NL = "Waterstof Helium Lithium Beryllium Boor Koolstof Stikstof Zuurstof Fluor Neon Natrium Magnesium Aluminium Silicium Fosfor Zwavel Chloor Argon Kalium Calcium Scandium Titanium Vanadium Chroom Mangaan IJzer Kobalt Nikkel Koper Zink Gallium Germanium Arseen Seleen Broom Krypton Rubidium Strontium Yttrium Zirkonium Niobium Molybdeen Technetium Ruthenium Rhodium Palladium Zilver Cadmium Indium Tin Antimoon Telluur Jood Xenon Cesium Barium Lanthaan Cerium Praseodymium Neodymium Promethium Samarium Europium Gadolinium Terbium Dysprosium Holmium Erbium Thulium Ytterbium Lutetium Hafnium Tantaal Wolfraam Renium Osmium Iridium Platina Goud Kwik Thallium Lood Bismut Polonium Astaat Radon Francium Radium Actinium Thorium Protactinium Uranium Neptunium Plutonium Americium Curium Berkelium Californium Einsteinium Fermium Mendelevium Nobelium Lawrencium Rutherfordium Dubnium Seaborgium Bohrium Hassium Meitnerium Darmstadtium Roentgenium Copernicium Nihonium Flerovium Moscovium Livermorium Tennessine Oganesson".split(" ");
  var PT_FR = "Hydrogène Hélium Lithium Béryllium Bore Carbone Azote Oxygène Fluor Néon Sodium Magnésium Aluminium Silicium Phosphore Soufre Chlore Argon Potassium Calcium Scandium Titane Vanadium Chrome Manganèse Fer Cobalt Nickel Cuivre Zinc Gallium Germanium Arsenic Sélénium Brome Krypton Rubidium Strontium Yttrium Zirconium Niobium Molybdène Technétium Ruthénium Rhodium Palladium Argent Cadmium Indium Étain Antimoine Tellure Iode Xénon Césium Baryum Lanthane Cérium Praséodyme Néodyme Prométhium Samarium Europium Gadolinium Terbium Dysprosium Holmium Erbium Thulium Ytterbium Lutécium Hafnium Tantale Tungstène Rhénium Osmium Iridium Platine Or Mercure Thallium Plomb Bismuth Polonium Astate Radon Francium Radium Actinium Thorium Protactinium Uranium Neptunium Plutonium Américium Curium Berkélium Californium Einsteinium Fermium Mendélévium Nobélium Lawrencium Rutherfordium Dubnium Seaborgium Bohrium Hassium Meitnerium Darmstadtium Roentgenium Copernicium Nihonium Flérovium Moscovium Livermorium Tennesse Oganesson".split(" ");
  var PT_MAIN = [
    [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
    [3,4,0,0,0,0,0,0,0,0,0,0,5,6,7,8,9,10],
    [11,12,0,0,0,0,0,0,0,0,0,0,13,14,15,16,17,18],
    [19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36],
    [37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54],
    [55,56,0,72,73,74,75,76,77,78,79,80,81,82,83,84,85,86],
    [87,88,0,104,105,106,107,108,109,110,111,112,113,114,115,116,117,118]
  ];

  function ptCategory(z) {
    if (z >= 57 && z <= 71) return "lanthanide";
    if (z >= 89 && z <= 103) return "actinide";
    if ([3, 11, 19, 37, 55, 87].indexOf(z) >= 0) return "alkali";
    if ([4, 12, 20, 38, 56, 88].indexOf(z) >= 0) return "alkaline";
    if ((z >= 21 && z <= 30) || (z >= 39 && z <= 48) || (z >= 72 && z <= 80) || (z >= 104 && z <= 112)) return "transition";
    if ([13, 31, 49, 50, 81, 82, 83, 113, 114, 115, 116].indexOf(z) >= 0) return "post";
    if ([5, 14, 32, 33, 51, 52, 84].indexOf(z) >= 0) return "metalloid";
    if ([9, 17, 35, 53, 85, 117].indexOf(z) >= 0) return "halogen";
    if ([2, 10, 18, 36, 54, 86, 118].indexOf(z) >= 0) return "noble";
    return "nonmetal";
  }

  function buildPeriodic(form, out, pack) {
    var names = pack.loc === "nl" ? PT_NL : pack.loc === "fr" ? PT_FR : PT_EN;
    var copy = {
      en: { mass: "Molar mass", massNote: "Mass number of a long-lived isotope, not a standard atomic weight.", period: "Period", group: "Group", family: "Family", fblock: "f-block", alkali: "Alkali metal", alkaline: "Alkaline earth metal", transition: "Transition metal", post: "Post-transition metal", metalloid: "Metalloid", nonmetal: "Nonmetal", halogen: "Halogen", noble: "Noble gas", lanthanide: "Lanthanide", actinide: "Actinide", state: "State", at: "at", solid: "Solid", liquid: "Liquid", gas: "Gas", unknown: "Not tabulated", noMb: "Melting and boiling points are not tabulated. The state shown is the standard one.", shells: "Electron shells", config: "Configuration", eneg: "Electronegativity", melt: "Melting point", boil: "Boiling point", ion: "Ionisation, 1st", eaff: "Electron affinity", density: "Density", found: "Described by", wiki: "Wikipedia", temp: "Temperature" },
      nl: { mass: "Molaire massa", massNote: "Massagetal van een langlevend isotoop, geen standaard atoomgewicht.", period: "Periode", group: "Groep", family: "Familie", fblock: "f-blok", alkali: "Alkalimetaal", alkaline: "Aardalkalimetaal", transition: "Overgangsmetaal", post: "Post-overgangsmetaal", metalloid: "Metalloïde", nonmetal: "Niet-metaal", halogen: "Halogeen", noble: "Edelgas", lanthanide: "Lanthanide", actinide: "Actinide", state: "Toestand", at: "bij", solid: "Vast", liquid: "Vloeibaar", gas: "Gas", unknown: "Niet getabelleerd", noMb: "Smelt- en kookpunt staan niet in de tabel. De toestand is die bij standaardomstandigheden.", shells: "Elektronenschillen", config: "Configuratie", eneg: "Elektronegativiteit", melt: "Smeltpunt", boil: "Kookpunt", ion: "Ionisatie, 1e", eaff: "Elektronenaffiniteit", density: "Dichtheid", found: "Beschreven door", wiki: "Wikipedia", temp: "Temperatuur" },
      fr: { mass: "Masse molaire", massNote: "Nombre de masse d'un isotope à vie longue, pas une masse atomique standard.", period: "Période", group: "Groupe", family: "Famille", fblock: "bloc f", alkali: "Métal alcalin", alkaline: "Métal alcalino-terreux", transition: "Métal de transition", post: "Métal pauvre", metalloid: "Métalloïde", nonmetal: "Non-métal", halogen: "Halogène", noble: "Gaz noble", lanthanide: "Lanthanide", actinide: "Actinide", state: "État", at: "à", solid: "Solide", liquid: "Liquide", gas: "Gaz", unknown: "Non tabulé", noMb: "Les points de fusion et d'ébullition ne sont pas dans le tableau. L'état indiqué est celui dans les conditions normales.", shells: "Couches électroniques", config: "Configuration", eneg: "Électronégativité", melt: "Point de fusion", boil: "Point d'ébullition", ion: "Ionisation, 1re", eaff: "Affinité électronique", density: "Masse volumique", found: "Décrit par", wiki: "Wikipédia", temp: "Température" }
    }[pack.loc] || null;
    if (!copy) copy = { mass: "Molar mass", massNote: "Mass number of a long-lived isotope, not a standard atomic weight.", period: "Period", group: "Group", family: "Family", fblock: "f-block", alkali: "Alkali metal", alkaline: "Alkaline earth metal", transition: "Transition metal", post: "Post-transition metal", metalloid: "Metalloid", nonmetal: "Nonmetal", halogen: "Halogen", noble: "Noble gas", lanthanide: "Lanthanide", actinide: "Actinide", state: "State", at: "at", solid: "Solid", liquid: "Liquid", gas: "Gas", unknown: "Not tabulated", noMb: "Melting and boiling points are not tabulated. The state shown is the standard one.", shells: "Electron shells", config: "Configuration", eneg: "Electronegativity", melt: "Melting point", boil: "Boiling point", ion: "Ionisation, 1st", eaff: "Electron affinity", density: "Density", found: "Described by", wiki: "Wikipedia", temp: "Temperature" };
    var place = {};
    PT_MAIN.forEach(function (line, pi) {
      line.forEach(function (z, gi) { if (z) place[z] = { period: pi + 1, group: gi + 1 }; });
    });
    for (var lz = 57; lz <= 71; lz++) place[lz] = { period: 6, group: null };
    for (var az = 89; az <= 103; az++) place[az] = { period: 7, group: null };
    var wrap = el("div", { class: "lx-pt-wrap" });
    var grid = el("div", { class: "lx-pt" });
    var detail = el("div", { class: "lx-pt__detail" });
    detail.hidden = true;
    var tip = el("div", { class: "lx-pt__tip" });
    tip.hidden = true;
    var picked = null;
    var tempC = 20;
    var factsTable = (typeof window !== "undefined" && window.LX_PT_FACT) || [];
    function facts(z) {
      var row = PT[z - 1];
      var extra = factsTable[z - 1] || {};
      var cat = ptCategory(z);
      var where = place[z] || { period: "", group: null };
      var mass = row[2] ? "[" + formatEU(row[1]) + "]" : formatEU(row[1]) + " g/mol";
      return { sym: row[0], name: names[z - 1], mass: mass, note: row[2] ? copy.massNote : "", cat: copy[cat], catKey: cat, period: where.period, group: where.group, extra: extra };
    }
    function celsius(kelvin) {
      return kelvin == null ? null : kelvin - 273.15;
    }
    function stateAt(extra, celsiusNow) {
      var k = celsiusNow + 273.15;
      var melt = extra.m;
      var boil = extra.b;
      if (melt == null && boil == null) {
        if (extra.p === "G") return copy.gas;
        if (extra.p === "L") return copy.liquid;
        if (extra.p === "S") return copy.solid;
        return copy.unknown;
      }
      if (boil != null && k >= boil) return copy.gas;
      if (melt != null && k < melt) return copy.solid;
      if (melt != null && (boil == null || k < boil)) return copy.liquid;
      if (boil != null && k < boil) return copy.liquid;
      return copy.unknown;
    }
    function clampTemp(c) {
      if (!Number.isFinite(c)) return null;
      return Math.min(4000, Math.max(-270, c));
    }
    function numOrDash(value, suffix) {
      if (value == null || value === "") return "—";
      return formatEU(value) + (suffix || "");
    }
    function paint(box, z) {
      var f = facts(z);
      box.textContent = "";
      box.appendChild(el("strong", { text: f.sym + "  " + f.name }));
      box.appendChild(el("p", { text: "Z " + z + " · " + copy.mass + " " + f.mass }));
      var where = copy.period + " " + f.period + (f.group ? " · " + copy.group + " " + f.group : " · " + copy.fblock);
      box.appendChild(el("p", { text: where }));
      box.appendChild(el("p", { text: copy.family + ": " + f.cat }));
      if (f.note) box.appendChild(el("p", { text: f.note }));
    }
    function cell(label, value) {
      var wrap = el("div");
      wrap.appendChild(el("span", { class: "lx-pt__k", text: label }));
      wrap.appendChild(el("span", { class: "lx-pt__v", text: value }));
      return wrap;
    }
    function paintDetail(z) {
      var f = facts(z);
      var extra = f.extra;
      detail.textContent = "";
      var hero = el("div", { class: "lx-pt__hero" });
      hero.setAttribute("data-cat", f.catKey);
      hero.appendChild(el("b", { text: String(z) }));
      hero.appendChild(el("strong", { text: f.sym }));
      hero.appendChild(el("span", { text: f.name }));
      hero.appendChild(el("em", { text: f.mass }));
      var body = el("div", { class: "lx-pt__body" });
      var state = el("em", { text: "" });
      function writeState() {
        var named = stateAt(extra, tempC);
        if (extra.m == null && extra.b == null) {
          state.textContent = copy.state + ": " + named + ". " + copy.noMb;
          return;
        }
        state.textContent = copy.state + " " + copy.at + " " + formatEU(tempC) + " °C: " + named;
      }
      if (extra.m != null || extra.b != null) {
        var temp = el("div", { class: "lx-pt__temp" });
        temp.appendChild(el("span", { text: copy.temp }));
        var range = el("input", { type: "range", min: "-270", max: "4000", step: "1", value: String(Math.round(tempC)), "aria-label": copy.temp });
        var typed = el("input", {
          type: "text",
          class: "lx-pt__temp-num",
          inputmode: "decimal",
          autocomplete: "off",
          "aria-label": copy.temp,
          value: formatEU(tempC)
        });
        function applyTemp(c, fromType) {
          var next = clampTemp(c);
          if (next == null) {
            typed.value = formatEU(tempC);
            return;
          }
          tempC = next;
          range.value = String(Math.round(tempC));
          if (!fromType) typed.value = formatEU(tempC);
          writeState();
        }
        range.addEventListener("input", function () { applyTemp(Number(range.value), false); });
        typed.addEventListener("change", function () { applyTemp(parseNum(typed.value), false); });
        typed.addEventListener("keydown", function (ev) {
          if (ev.key === "Enter") {
            ev.preventDefault();
            applyTemp(parseNum(typed.value), false);
          }
        });
        temp.appendChild(range);
        temp.appendChild(typed);
        temp.appendChild(el("span", { text: "°C" }));
        body.appendChild(temp);
      }
      writeState();
      body.appendChild(state);
      var gridFacts = el("div", { class: "lx-pt__facts" });
      gridFacts.appendChild(cell(copy.family, f.cat));
      gridFacts.appendChild(cell(copy.period, String(f.period)));
      gridFacts.appendChild(cell(copy.group, f.group ? String(f.group) : copy.fblock));
      gridFacts.appendChild(cell(copy.shells, (extra.sh || []).join(", ") || "—"));
      gridFacts.appendChild(cell(copy.config, extra.cfg || "—"));
      gridFacts.appendChild(cell(copy.eneg, numOrDash(extra.en, "")));
      gridFacts.appendChild(cell(copy.melt, numOrDash(celsius(extra.m), " °C")));
      gridFacts.appendChild(cell(copy.boil, numOrDash(celsius(extra.b), " °C")));
      gridFacts.appendChild(cell(copy.ion, numOrDash(extra.ion, " kJ/mol")));
      gridFacts.appendChild(cell(copy.eaff, numOrDash(extra.ea, " kJ/mol")));
      var densityUnit = extra.g ? " g/L" : " g/cm³";
      gridFacts.appendChild(cell(copy.density, extra.d == null ? "—" : formatEU(extra.d) + densityUnit));
      if (extra.who) gridFacts.appendChild(cell(copy.found, extra.who));
      body.appendChild(gridFacts);
      if (f.note) body.appendChild(el("p", { class: "lx-pt__note", text: f.note }));
      if (extra.wiki) {
        var link = el("a", { href: extra.wiki, text: copy.wiki });
        link.target = "_blank";
        link.rel = "noopener";
        body.appendChild(link);
      }
      detail.appendChild(hero);
      detail.appendChild(body);
    }
    function placeTip(btn) {
      var rect = btn.getBoundingClientRect();
      tip.hidden = false;
      var left = Math.min(rect.left, window.innerWidth - 300);
      var top = rect.bottom + 8;
      if (top + 140 > window.innerHeight) top = Math.max(8, rect.top - 150);
      tip.style.left = Math.max(8, left) + "px";
      tip.style.top = top + "px";
    }
    function showTip(z, btn) {
      paint(tip, z);
      placeTip(btn);
    }
    function selectEl(z, btn) {
      if (picked) picked.classList.remove("is-on");
      picked = btn;
      btn.classList.add("is-on");
      detail.hidden = false;
      paintDetail(z);
      tip.hidden = true;
    }
    function buttonFor(z) {
      var row = PT[z - 1];
      var btn = el("button", { type: "button", "data-cat": ptCategory(z), "data-z": String(z) });
      btn.appendChild(el("small", { text: String(z) }));
      btn.appendChild(el("strong", { text: row[0] }));
      btn.addEventListener("mouseenter", function () { showTip(z, btn); });
      btn.addEventListener("focus", function () { showTip(z, btn); });
      btn.addEventListener("click", function () { selectEl(z, btn); });
      btn.addEventListener("mouseleave", function () { tip.hidden = true; });
      btn.addEventListener("blur", function () { tip.hidden = true; });
      return btn;
    }
    PT_MAIN.forEach(function (line) {
      line.forEach(function (z) {
        if (!z) grid.appendChild(el("div", { class: "lx-pt__gap" }));
        else grid.appendChild(buttonFor(z));
      });
    });
    wrap.appendChild(grid);
    function frow(from, to) {
      var row = el("div", { class: "lx-pt lx-fblock" });
      for (var z = from; z <= to; z++) row.appendChild(buttonFor(z));
      wrap.appendChild(row);
    }
    frow(57, 71);
    frow(89, 103);
    var wikiByLoc = {
      en: {
        alkali: "https://en.wikipedia.org/wiki/Alkali_metal",
        alkaline: "https://en.wikipedia.org/wiki/Alkaline_earth_metal",
        transition: "https://en.wikipedia.org/wiki/Transition_metal",
        post: "https://en.wikipedia.org/wiki/Post-transition_metal",
        metalloid: "https://en.wikipedia.org/wiki/Metalloid",
        nonmetal: "https://en.wikipedia.org/wiki/Nonmetal",
        halogen: "https://en.wikipedia.org/wiki/Halogen",
        noble: "https://en.wikipedia.org/wiki/Noble_gas",
        lanthanide: "https://en.wikipedia.org/wiki/Lanthanide",
        actinide: "https://en.wikipedia.org/wiki/Actinide"
      },
      nl: {
        alkali: "https://nl.wikipedia.org/wiki/Alkalimetaal",
        alkaline: "https://nl.wikipedia.org/wiki/Aardalkalimetaal",
        transition: "https://nl.wikipedia.org/wiki/Overgangsmetaal",
        post: "https://nl.wikipedia.org/wiki/Hoofdgroepmetaal",
        metalloid: "https://nl.wikipedia.org/wiki/Metalloïde",
        nonmetal: "https://nl.wikipedia.org/wiki/Niet-metaal",
        halogen: "https://nl.wikipedia.org/wiki/Halogeen",
        noble: "https://nl.wikipedia.org/wiki/Edelgas",
        lanthanide: "https://nl.wikipedia.org/wiki/Lanthanoïde",
        actinide: "https://nl.wikipedia.org/wiki/Actinoïde"
      },
      fr: {
        alkali: "https://fr.wikipedia.org/wiki/Métal_alcalin",
        alkaline: "https://fr.wikipedia.org/wiki/Métal_alcalino-terreux",
        transition: "https://fr.wikipedia.org/wiki/Métal_de_transition",
        post: "https://fr.wikipedia.org/wiki/Métal_pauvre",
        metalloid: "https://fr.wikipedia.org/wiki/Métalloïde",
        nonmetal: "https://fr.wikipedia.org/wiki/Non-métal",
        halogen: "https://fr.wikipedia.org/wiki/Halogène",
        noble: "https://fr.wikipedia.org/wiki/Gaz_noble",
        lanthanide: "https://fr.wikipedia.org/wiki/Lanthanide",
        actinide: "https://fr.wikipedia.org/wiki/Actinide"
      }
    };
    var wiki = wikiByLoc[pack.loc] || wikiByLoc.en;
    var legend = el("ul", { class: "lx-pt__legend" });
    ["alkali", "alkaline", "transition", "post", "metalloid", "nonmetal", "halogen", "noble", "lanthanide", "actinide"].forEach(function (cat) {
      var li = el("li");
      var sw = el("span", { class: "lx-pt__swatch" });
      sw.setAttribute("data-cat", cat);
      li.appendChild(sw);
      var link = el("a", { href: wiki[cat], text: copy[cat] });
      link.target = "_blank";
      link.rel = "noopener";
      li.appendChild(link);
      legend.appendChild(li);
    });
    form.appendChild(wrap);
    form.appendChild(legend);
    form.appendChild(detail);
    form.appendChild(tip);
    var opener = grid.querySelector('button[data-z="1"]');
    if (opener) selectEl(1, opener);
  }

  return {
    parseNum: parseNum,
    formatEU: formatEU,
    roundSig: roundSig,
    molarityOfBottle: molarityOfBottle,
    massFromMolar: massFromMolar,
    bufferPair: bufferPair,
    serialRows: serialRows,
    toGramsPerL: toGramsPerL,
    fromGramsPerL: fromGramsPerL,
    toLitres: toLitres,
    fromLitres: fromLitres,
    CONC: CONC,
    rcfFromRpm: rcfFromRpm,
    rpmFromRcf: rpmFromRcf,
    antoineP: antoineP,
    antoineT: antoineT,
    aFromPercentT: aFromPercentT,
    percentTFromA: percentTFromA,
    awgMm: awgMm,
    hplcTransfer: hplcTransfer,
    solubilityAt: solubilityAt,
    boot: boot
  };
});
